import { createContext, Fragment, useCallback, useContext, useEffect, useId, useMemo, useRef, useState, type ReactNode, useLayoutEffect } from 'react';
import { useBackdropDismiss } from '~/hooks/use-backdrop-dismiss';
import { ArrowsClockwise, MagnifyingGlass, CaretLeft, CaretRight, Check, CircleNotch, ClipboardText, Eraser, Eye, EyeSlash, FileText, FloppyDisk, Gear, Lightning, LinkSimple, NotePencil, Paperclip, Trash, Warning, X } from '@phosphor-icons/react';
import { toast } from 'sonner';
import { claimBuildWindow, openAppWindow, openFileWindow, openSettingsWindow, openUploadWindow } from '~/features/windows/window-payloads';
import { fileViewKind, openableFile, projectFileUrl, uploadIdFromImageSrc } from '~/features/windows/file-view';
import { fileWindowId, fileWindowTitle } from '~/features/windows/window-titles';
import { useWindowStore } from '~/lib/stores/window-store';
import { cn } from '~/lib/utils';
import { Button } from '~/components/ui/button';
import { ActionBar } from '~/components/ui/action-bar';
import { AUTO_CLEAR_CHOICES, autoClearChoiceOf, autoClearRequestFor, isDefaultAutoClearChoice } from './auto-clear-choice';
import { useCanBuildApps } from '~/hooks/queries/use-is-admin';
import { useFeatureFlag, CODING_CAPABILITIES_FLAG, CODING_DESIGN_DIRECTION_FLAG } from '~/hooks/queries/use-feature-flags';
import { useCloseOnOperatorPanelRequest } from '~/lib/stores/use-close-on-operator-panel-request';
import { bottomScrollTop, OPEN_SETTLE_MS, shouldRepinOnGrowth } from '~/features/chat/open-at-bottom-logic';
import { WindowShell, useWindowChrome } from '~/features/windows/window-system';
import { WindowMinimizeButton } from '~/features/windows/window-minimize-button';
import { WindowPopOutButton } from '~/features/windows/window-pop-out-button';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '~/components/ui/dialog';
import { MarkdownRenderer } from '~/components/markdown-renderer';
import { EddyLoadingMark, EddyMark } from '~/components/eddy-mark';
import { LIFT_SLIDE_MS, liftStyle, liftTransition, useCenterLift, useExpandDrop } from '~/lib/use-center-lift';
import { useUiRefactoring } from '~/lib/ui-refactoring';
import { ChatInput, type ChatAttachment, type ChatInputHandle } from '~/features/chat/chat-input';
import {
  READING_COLUMN_PX,
  READING_FONT_PX,
  type ReadingMaxWidth,
  useReadingPrefs,
} from '~/features/chat/reading-prefs';
import {
  useCodingDraftUpdate,
  useCodingSessions,
  useCodingSessionContext,
} from '~/hooks/queries/use-coding-sessions';
import {
  useClearCodingSession,
  useCreateCodingSession,
  useDeleteCodingSession,
  useHandoverCodingSession,
  usePublishCodingSession,
  useSaveCodingSession,
  useSetCodingAutoClear,
  useSetCodingSessionContext,
} from '~/hooks/mutations/use-coding-mutations';
import { useQueryClient } from '@tanstack/react-query';
import { codingKeys } from '~/lib/query-keys';
import { openAppUrl } from '~/lib/open-app-url';
import { useConfirm } from '~/components/confirm-dialog';
import { buildLiveness, STUCK_LABEL, type BuildLiveness } from './build-liveness';
import { useCodingSession, type SessionSummaryState } from './use-coding-session';
import { foldEarlierHistory, foldLabel } from './history-fold';
import { buildsListFloats, ExistingBuildsTimeline } from './existing-builds-timeline';
import {
  buildStatusLabel,
  formatDuration,
  formatRunSummary,
  formatSessionDuration,
  formatSessionCost,
  formatSessionTurns,
  type RunSummary,
  type CodingQuestionAnswer,
  type CodingStatus,
  type RunActivity,
  type TranscriptItem,
  type UploadAttachment,
  type WaitingReason,
  appAudience,
  APP_AUDIENCE_COPY,
  findAnswerPanelItem,
} from './coding-events';
import { earlierActionsLabel, groupActivityRuns, type ActivityItem } from './activity-runs';
import { createdIn, firstCreations } from './created-files';
import { conversationImages, stepIndex, type ConversationImage } from './conversation-images';
import { AppSharePopover } from '~/features/apps/app-share-control';
import { AskOwnerToAcceptButton, publishControlFor } from '~/features/apps/ask-to-accept';
import { DraftUpdateButton, draftUpdateGate, overlapNames } from './draft-update-button';
import { ownerDraftLink, previewEye } from './preview-eye';
import { BuildFileDropOverlay, useBuildFileDrop } from './build-file-drop';
import { CodingQuestionsCard } from './build-questions-card';
import { CodingAnswerPanel } from './coding-answer-panel';
import {
  answerPlaceholder,
  answerSendGate,
  isDetachedBatch,
  panelTakesTypedAnswer,
  planComposerSend,
} from './answer-panel';
import {
  emptyWizardValue,
  fitWizardValue,
  wizardCounts,
  wizardEntries,
  type WizardValue,
} from '../questions/answer-wizard-model';
import { ProjectContextDialog } from './project-context-dialog';
import { ConfigurationSections } from './configuration-sections';
import { BuildSettingsPanel, PanelAction, PanelSection } from './build-settings-panel';
import { DesignDirectionDisclosure, type UploadInFlight } from './design-direction-section';
import { buildDesignCreatePayload, EMPTY_DESIGN_DRAFT, type DesignDirectionDraft } from './design-direction';
import { NEW_PROJECT_QUESTIONS, newProjectContext, newProjectStartGate } from './new-project-questions';
import { AnswerWizard } from '../questions/answer-wizard';
import { submitComposerAnswer } from './composer-answer';
import { sendFailureMessage } from './send-failure';
import { Tip } from '~/components/ui/tip';
import { ContextMeter } from '~/components/builder-header/context-meter';
import { HeaderPreviewLink } from '~/components/builder-header/builder-header';
import { ESTIMATE_HINT } from '~/features/limits/compute-blocks';

/**
 * Zen-style coding window (FRONT-333), visually matched 1:1 to the inbox
 * chat modal: same backdrop, card, eyebrow header, reading column (its
 * width + font size are the same shared prefs as the inbox modal's "Aa"
 * popover — FRONT-1668 moved this window's own control for them into the
 * "…" menu's "Appearance" item, opening a dialog instead of a popover; the
 * inbox modal's popover is unchanged), and the same ChatInput composer
 * (text + file uploads, which land in the project's uploads folder —
 * FRONT-358). The window is a VIEW onto a background coding session —
 * closing it (×, backdrop, Esc) only detaches; the run keeps going
 * server-side, and reopening replays the tape and catches up.
 *
 * Opens on the most recent session; a header switcher jumps between
 * projects or starts a new one (one short "name your project" step).
 * `initialSessionId` (FRONT-334) pre-attaches a specific session — the Files
 * page passes it when a saved code-session document is opened, so the window
 * resumes exactly the session the document journals.
 */

function CodingSessionModalBody({
  onClose,
  initialSessionId,
  startFresh = false,
}: {
  onClose: () => void;
  initialSessionId?: string;
  startFresh?: boolean;
}) {
  // FRONT-3146: docked in the window layer or the standalone full-screen overlay.
  const chrome = useWindowChrome();
  const { data: sessions, isLoading: sessionsLoading, error: sessionsError } = useCodingSessions(true);
  const [selectedId, setSelectedId] = useState<string | null>(initialSessionId ?? null);
  // Build always starts a new build from the top bar (eddy, 2026-10-26).
  const [creating, setCreating] = useState(startFresh && !initialSessionId);

  // FRONT-1787: the "Project context" editor, opened from the settings panel.
  const [contextOpen, setContextOpen] = useState(false);
  // The one cog (eddy, 2026-10-26): sharing, Save App, Configuration and the
  // project actions in a panel inside this window — never a modal.
  const [settingsOpen, setSettingsOpen] = useState(false);

  const [prefs] = useReadingPrefs();
  const columnMax = READING_COLUMN_PX[prefs.width];
  // FRONT-3223 (eddy, 2026-10-28): on a new Build the list of builds floats
  // over the window's left edge when there is room for it beside a centred
  // column, so the content is centred over the whole window, as an opened
  // Build's is — and when the list fades out on the first send, nothing moves.
  // Narrower windows keep the list in the flow (it would cover the content).
  const bodyRef = useRef<HTMLDivElement>(null);
  const [bodyWidth, setBodyWidth] = useState(0);
  useLayoutEffect(() => {
    const el = bodyRef.current;
    if (!el) return;
    const measure = () => setBodyWidth(el.clientWidth);
    measure();
    if (typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  const buildsFloat = buildsListFloats(columnMax, bodyWidth);
  // The new project is being created: the list fades while the box slides down.
  const [leavingNew, setLeavingNew] = useState(false);
  const chatFont = READING_FONT_PX[prefs.fontSize];

  // Land on the most recent session once the list arrives; if there are no
  // sessions yet, the new-project step is the only thing to show.
  useEffect(() => {
    if (!sessions || selectedId || creating) return;
    if (sessions.length > 0) setSelectedId(sessions[0].id);
    else setCreating(true);
  }, [sessions, selectedId, creating]);

  const {
    session,
    refreshApp,
    transcript,
    openedAtSeq,
    summary,
    status,
    displayStatus,
    displayWaitingReason,
    queueBusy,
    composerBusy,
    loadError,
    uploads,
    contextUsage,
    activity,
    runSummary,
    sessionStats,
    sessionCost,
    waitingReason,
    send,
    updateDraft,
    interrupt,
    deleteUpload,
    answerQuestions,
    blockingQuestion: openQuestion,
  } = useCodingSession(creating ? null : selectedId);

  // FRONT-2935: "Updates available" — the app has changes this draft lacks.
  const { data: draftUpdateAvailable } = useCodingDraftUpdate(creating ? null : selectedId, !creating && !!session?.app);
  const [updatingDraft, setUpdatingDraft] = useState(false);
  const draftGate = draftUpdateGate({ available: draftUpdateAvailable === true, runBusy: queueBusy, pending: updatingDraft });
  const handleUpdateDraft = useCallback(async () => {
    setUpdatingDraft(true);
    try {
      const result = await updateDraft();
      if (!result) return;
      if (result.status === 'reworking') toast(`EDDY is bringing the app's latest changes together with your work in ${overlapNames(result.files, result.more)}.`);
      else if (result.status === 'updated') toast.success("Your draft now has the app's latest changes.");
      else toast("Your draft already has the app's latest changes.");
    } catch (err) {
      toast.error((err as Error).message);
    } finally {
      setUpdatingDraft(false);
    }
  }, [updateDraft]);
  // Pictures — EDDY's screenshots, images in a message — open over this
  // window rather than in a window of their own (eddy, 2026-10-27).
  const [viewerImage, setViewerImage] = useState<BuildImage | null>(null);
  useEffect(() => setViewerImage(null), [session?.id]);

  // FRONT-3171: "Wrote BRIEF.md" → the file in its own window, beside this one.
  // Outside the window layer (the pop-out) it opens in a browser tab instead.
  const openProjectFile = useCallback(
    (path: string) => {
      if (!session) return;
      if (fileViewKind(path) === 'image') {
        const sessionId = session.id;
        setViewerImage({ src: projectFileUrl(sessionId, path), name: path.split('/').pop() || path });
        return;
      }
      if (chrome.docked) openFileWindow(session.id, path, chrome.windowId);
      else window.open(projectFileUrl(session.id, path), '_blank', 'noopener');
    },
    [session, chrome],
  );
  const showImage = useCallback((image: BuildImage) => setViewerImage(image), []);
  // Every picture in this conversation, in order: the viewer's previous and next step through them.
  const conversationPictures = useMemo(
    () => conversationImages(transcript, firstCreations(transcript), session?.id ?? null),
    [transcript, session?.id],
  );

  // FRONT-3155: the open question batch is answered from a panel attached to
  // the reply box, never from the transcript card. It shows for ANY open batch
  // (not only one the run is blocked on — answering a timed-out card later
  // starts a continuation), but not once the run died (FRONT-2106) and not for
  // a batch this window already sent an answer for, whose tape event is still
  // in flight.
  const [answerDraft, setAnswerDraft] = useState<{ batchId: number; value: WizardValue } | null>(null);
  const [sentBatchIds, setSentBatchIds] = useState<number[]>([]);
  const panelItem = useMemo(
    () => findAnswerPanelItem(transcript, status, sentBatchIds),
    [transcript, status, sentBatchIds],
  );
  const panelValue = panelItem
    ? fitWizardValue(answerDraft?.batchId === panelItem.batchId ? answerDraft.value : undefined, panelItem.questions.length)
    : null;
  // A batch split off by a partial answer has no run reading it; while the run
  // works, an answer would be recorded for nobody (the server refuses it too).
  const panelDetachedAndBusy =
    !!panelItem && isDetachedBatch(panelItem) && (status === 'working' || status === 'checking');
  const panelSendGate = useCallback(
    (typed: string) =>
      panelItem && panelValue
        ? answerSendGate({ counts: wizardCounts(panelValue), typedText: typed, detachedAndBusy: panelDetachedAndBusy })
        : { disabled: !typed.trim() },
    [panelItem, panelValue, panelDetachedAndBusy],
  );

  // FRONT-1787: the attached project's standing context, for the transcript
  // note. One file read per open session, deliberately not folded into the
  // sessions list (a hot poll).
  const { data: contextRead } = useCodingSessionContext(creating ? null : selectedId);
  // The note renders nothing when the read failed — same as no context. The
  // EDITOR is where the difference matters (see ProjectContextDialog).
  const projectContext = contextRead?.readable ? contextRead.context : null;

  // FRONT-648 / FRONT-2884: optimistic auto-handover state, synced from the
  // session row — "off" or the threshold % it fires at.
  const serverAutoClear = autoClearChoiceOf(session);
  const [autoClearChoice, setAutoClearChoice] = useState(serverAutoClear);
  useEffect(() => {
    setAutoClearChoice(serverAutoClear);
  }, [serverAutoClear]);

  // FRONT-3084: the composer stays free during the background check (a message
  // stops it), but the actions that need an idle session still wait for it.
  const actionsBusy = queueBusy || status === 'checking';

  // FRONT-1898: the composer already frees itself the instant this fires
  // (composerBusy/queueBusy fold in the manual-stop override inside
  // use-coding-session.ts) — this toast only covers a session that is not
  // running anywhere, which used to be a silent no-op with no signal at all.
  const stopRun = useCallback(() => {
    interrupt()
      .then((interrupted) => {
        if (!interrupted) {
          toast.error(
            "Nothing to stop — this session isn't running.",
          );
        }
      })
      .catch((err) => toast.error((err as Error).message || 'Could not stop the run'));
  }, [interrupt]);

  // FRONT-358: per-file delete from the uploads strip — removes the file
  // from the session repo and (via the tape event) from the visible list.
  const handleDeleteUpload = useCallback(
    (name: string) => {
      deleteUpload(name).catch((err) =>
        toast.error((err as Error).message || 'Could not remove the file'),
      );
    },
    [deleteUpload],
  );

  // Save the session to Files as its journal document (FRONT-334).
  const save = useSaveCodingSession();
  const handleSave = useCallback(() => {
    if (!session || save.isPending) return;
    save.mutate(session.id, {
      onSuccess: (result) => {
        // FRONT-1668: the menu item reads "Save Conversation" now, so the
        // toast says the same thing — it's still a Files document under the
        // hood (session-document.ts), just described by what it holds
        // rather than where it lands.
        toast.success(
          result.updated
            ? 'Conversation updated in Files'
            : 'Conversation saved to Files — find it under the code-session badge',
        );
      },
      onError: (err) => toast.error((err as Error).message || 'Could not save the session'),
    });
  }, [session, save]);

  // Generate a handover brief (FRONT-646) — an LLM carry-forward summary stored
  // on the session; the seed for "Clear & continue". Refused mid-run server-side.
  const handover = useHandoverCodingSession();
  const handleHandover = useCallback(() => {
    if (!session || handover.isPending) return;
    handover.mutate(session.id, {
      onSuccess: (result) => {
        toast.success('Handover ready — saved for Clear & continue', {
          description: result.handover.slice(0, 200) + (result.handover.length > 200 ? '…' : ''),
          duration: 8000,
        });
      },
      onError: (err) => toast.error((err as Error).message || 'Could not generate the handover'),
    });
  }, [session, handover]);

  // Clear & continue (FRONT-647): reset Eddy's context; the next message
  // continues from the latest handover. The backend requires a handover first.
  const clear = useClearCodingSession();
  const handleClear = useCallback(() => {
    if (!session || clear.isPending) return;
    clear.mutate(session.id, {
      onSuccess: () => {
        toast.success('Context cleared — your next message continues from the handover');
      },
      onError: (err) => toast.error((err as Error).message || 'Could not clear the context'),
    });
  }, [session, clear]);

  // FRONT-2776: delete the attached project, then land on the next one (or
  // the new-project step). Selection moves explicitly: the list still holds
  // the deleted row until its refetch lands, and the auto-select effect would
  // pick it straight back.
  const confirm = useConfirm();
  const deleteSession = useDeleteCodingSession();
  const handleDelete = useCallback(async () => {
    if (!session || deleteSession.isPending) return;
    const ok = await confirm({
      title: `Delete project "${session.title}"?`,
      description: session.app
        ? 'This deletes the conversation and the project files in your workspace. The published app stays as it is. Cannot be undone.'
        : 'This deletes the conversation and the project files in your workspace. Cannot be undone.',
      confirmText: 'Delete project',
      destructive: true,
    });
    if (!ok) return;
    const deletedId = session.id;
    deleteSession.mutate(deletedId, {
      onSuccess: () => {
        toast.success('Project deleted');
        // FRONT-3223 (eddy, 2026-10-28): deleting the project closes its window.
        setSettingsOpen(false);
        onClose();
      },
      onError: (err) => toast.error((err as Error).message || 'Could not delete the project'),
    });
  }, [session, deleteSession, confirm, onClose]);

  // Auto-handover + clear toggle (FRONT-648) — optimistic flip, reverts on error.
  // FRONT-2219 / FRONT-2473: the three built-in capability groups now live
  // in Configuration → "While building" (builder-capabilities.tsx). Fallback
  // true: the server still gates what a run may call, so a flags-endpoint
  // hiccup must not hide the switches.
  const capabilitiesFlagOn = useFeatureFlag(CODING_CAPABILITIES_FLAG, true);

  const autoClear = useSetCodingAutoClear();
  const handleAutoClearChoice = useCallback(
    (choice: string) => {
      if (!session || autoClear.isPending) return;
      // FRONT-3301: the "default" entry clears the session's own pick.
      const request = autoClearRequestFor(choice, autoClearChoice, session);
      if (!request) return;
      const previous = autoClearChoice;
      setAutoClearChoice(choice);
      autoClear.mutate(
        { sessionId: session.id, ...request },
        {
          onSuccess: () =>
            toast.success(
              choice === 'off'
                ? 'Auto-handover off'
                : `Auto-handover on — past ${choice}% context EDDY saves a handover and continues fresh`,
            ),
          onError: (err) => {
            setAutoClearChoice(previous);
            toast.error((err as Error).message || 'Could not change the setting');
          },
        },
      );
    },
    [session, autoClear, autoClearChoice],
  );

  // Save the project into Apps (FRONT-336, FRONT-1668, FRONT-1775). Every
  // call — first save or re-save — now goes live (status: "live"); who can
  // actually reach it is a separate axis this call never touches
  // (visibility/workspaceShared, governed by the Share control). The one
  // exception is an app the owner explicitly Archived: a save on that
  // session refuses rather than silently reviving it (pr-panel finding,
  // `CodingPublishError` code "archived").
  const saveApp = usePublishCodingSession();
  const handleSaveApp = useCallback(() => {
    if (!session || saveApp.isPending) return;
    saveApp.mutate(session.id, {
      onSuccess: (result) => {
        // The owner chose not to publish what the checks found.
        if (!result) return;
        // FRONT-1668 review: the agent's own publish_app tool routinely goes
        // live before the user ever touches this button (src/coding/prompt.ts
        // tells it to publish as soon as the build works), so a RE-save here
        // is ordinarily a save of an ALREADY-LIVE app — the description must
        // say what's actually true for the row that came back, not assume
        // "still just a draft" the way the first-save copy correctly can
        // (`updated: false` there guarantees a brand-new, unpublished row).
        //
        // FRONT-1879: it now says WHO CAN OPEN IT, from the server-derived
        // audience, rather than branching on lifecycle. The old copy read
        // "visible to anyone with the link" for ANY live app and "still
        // unpublished — only you can open it" otherwise: the first was false
        // for every unshared private app (FRONT-856 closed that link), the
        // second called a saved, servable, owner-only app "unpublished", and
        // neither could name the genuinely public case at all.
        // Fall back to the SMALLEST audience if the field is missing — a
        // response from an older backend, or a shape that changed under us.
        // `APP_AUDIENCE_COPY[undefined]` would throw inside this success
        // handler and swallow the whole toast, so the user would be told
        // nothing at all about an app that did save. Failing to the smaller
        // audience is the same safe direction `recoverShareChoice` takes on
        // the backend: understating who can open it is recoverable, and the
        // Apps list shows the real answer either way.
        const audienceHint = (APP_AUDIENCE_COPY[result.audience] ?? APP_AUDIENCE_COPY['only-you']).hint;
        toast.success(result.updated ? 'App updated' : 'Saved to Apps', {
          description: result.updated
            ? `“${result.title}” now holds your latest changes. ${audienceHint}`
            : `“${result.title}” is in your Apps. ${audienceHint}`,
          action: {
            label: 'Open app',
            onClick: () => openAppUrl(result.url),
          },
          duration: 8000,
        });
        if (result.warnings.length > 0) {
          // FRONT-2027: informational, not a warning — the app published
          // fine, this just names two files that were kept out of the
          // bundle on purpose. `toast.warning` read as something went wrong.
          toast.info(result.warnings[0], { duration: 8000 });
        }
      },
      // A refusal can be two sentences long (a page changed in chat and here too): long enough to read.
      onError: (err) => toast.error((err as Error).message || 'Could not save the app', { duration: 15000 }),
    });
  }, [session, saveApp]);

  // FRONT-1668: this is a `window` keydown listener, so without a guard it
  // fires on EVERY Escape, including one meant for the Appearance dialog
  // stacked on top of this window. Two things were tried and rejected before
  // this one, both confirmed live (console-instrumented) rather than assumed:
  //
  // 1. A guard reading the `appearanceOpen` React flag. Radix's own Escape
  //    handling (`@radix-ui/react-dismissable-layer`'s `useEscapeKeydown`)
  //    listens on `document` in the CAPTURE phase and synchronously flips
  //    that flag to `false` (via `onOpenChange`) before the event reaches
  //    this BUBBLE-phase `window` listener — so the guard's own closure
  //    always saw it already `false` and did nothing.
  // 2. `if (e.defaultPrevented) return;` — the fix `inbox-chat-modal.tsx`
  //    uses for the identical class of bug (FRONT-1579): Radix calls
  //    `preventDefault()` (never `stopPropagation()`) right before
  //    dismissing the innermost layer, and reading that is immune to #1's
  //    race since it mutates the event object itself, synchronously, rather
  //    than depending on a re-render. This DID stop Escape from closing the
  //    Building Session while the Appearance dialog was open — but it
  //    introduced a NEW regression: a hover-triggered Radix Tooltip is ALSO
  //    a `DismissableLayer` and ALSO calls `preventDefault()` on Escape to
  //    dismiss itself, so this guard swallowed Escape whenever a tooltip
  //    merely happened to be showing — e.g. hovering the Close button below,
  //    whose own tooltip literally reads "Close (Esc)", so closing needed
  //    two presses instead of one.
  //
  // The fix that actually works for both cases: query the DOM for a genuine
  // open MODAL DIALOG (`role="dialog"`, which a Tooltip's `role="tooltip"`
  // never carries) — but read it in the CAPTURE phase, at `window`, which
  // runs BEFORE Radix's own document-level capture listener (capture order
  // is window → document → … → target). At that point nothing has been
  // dismissed yet, so the query reflects the TRUE pre-event state and can't
  // race the way reading it in the bubble phase did (confirmed live: the
  // same query read in the bubble phase already showed no open dialog by
  // the time it ran, for the same reason `appearanceOpen` above did).
  useEffect(() => {
    const onKey = (e: globalThis.KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      // FRONT-2219 QA: an open Radix menu (the Session actions dropdown,
      // role="menu") is a DismissableLayer too — Escape there must close the
      // menu only, never the whole build window. Seen live: Escape with the
      // capability switches open dropped the person back to the inbox.
      // Same selector the operator panel uses for the identical guard.
      if (
        document.querySelector(
          '[role="dialog"][data-state="open"], [role="alertdialog"][data-state="open"], [role="menu"][data-state="open"], [role="listbox"][data-state="open"]',
        )
      )
        return;
      // An open picture is the innermost layer: Escape closes it first.
      if (viewerImage) {
        setViewerImage(null);
        return;
      }
      // The settings panel is the next layer of this window: Escape
      // closes it first, and only the next one closes the window.
      if (settingsOpen) {
        setSettingsOpen(false);
        return;
      }
      chrome.escape(onClose);
    };
    window.addEventListener('keydown', onKey, { capture: true });
    return () => window.removeEventListener('keydown', onKey, { capture: true });
  }, [onClose, chrome, settingsOpen, viewerImage]);

  // FRONT-1991: modal viewers are mutually exclusive, never stacked
  // (CLAUDE.md). The desktop operator panel is a second full-screen
  // overlay, mounted separately in `app-layout.tsx`, that has no way to
  // close THIS window itself — this hook is the other half: it closes this
  // window the instant a "Watch desktop" click raises a request, rather
  // than sitting stacked underneath the new overlay.
  useCloseOnOperatorPanelRequest(onClose);
  // FRONT-3146: the Pro Builder window no longer closes this one — windows
  // coexist in the window layer (`lib/stores/window-rules.ts`).

  // Refresh the sessions list when this window closes, so the top bar's
  // activity board shows the session's new state immediately instead of on
  // its next poll tick. This used to also stamp "the user has seen the coding
  // window" for the composer icon's green "finished" dot (FRONT-339); that
  // icon and everything behind it went on 2026-09-01 with the Build button,
  // and the stamp went with it rather than staying as a write nobody reads.
  const queryClient = useQueryClient();
  useEffect(() => {
    return () => {
      queryClient.invalidateQueries({ queryKey: codingKeys.list() });
    };
  }, [queryClient]);

  // FRONT-2258: the state strip carried a copy-link button beside the app's
  // address. The one-row header has no room for the address itself, so the
  // ACTION moves into the "…" menu rather than being dropped — Preview opens
  // the app, this copies its link.
  //
  // Guard on the API being PRESENT, not `?.` — in an insecure context (http
  // on a LAN IP, some webviews) `navigator.clipboard` is undefined, and
  // `await navigator.clipboard?.writeText(...)` resolves to `await undefined`
  // and would falsely report a copy that never happened.
  const handleCopyAppLink = useCallback(async (url: string) => {
    if (!navigator.clipboard) {
      toast.error('Copying needs a secure (https) context');
      return;
    }
    try {
      await navigator.clipboard.writeText(url);
      toast.success('Link copied');
    } catch {
      toast.error('Could not copy the link');
    }
  }, []);

  // The dialog's accessible name.
  const title = creating ? 'New project' : session?.title ?? '…';
  // FRONT-3146: keep the window bar's label in step with the session's title.
  const { setTitle: setWindowTitle } = chrome;
  useEffect(() => {
    setWindowTitle(creating ? 'New build' : session?.title ?? 'Build');
  }, [creating, session?.title, setWindowTitle]);

  // Backdrop dismiss keys off where the press started (see use-backdrop-dismiss).
  const backdropDismiss = useBackdropDismiss(onClose);
  // FRONT-3146: a docked window has no backdrop to click and is not modal.
  const frameProps = chrome.docked ? {} : { 'aria-modal': true as const, ...backdropDismiss };

  // FRONT-3138: a desktop file dropped anywhere on the window — backdrop
  // included, which would otherwise open the file in the tab — goes to the
  // reply box, exactly as a drop on the box does: a build's, or the new
  // project's, where the files become the project's references.
  const composerRef = useRef<ChatInputHandle>(null);
  const { canBuildApps } = useCanBuildApps();
  const fileDrop = useBuildFileDrop({
    // FRONT-3138: the new-project screen is a reply box too.
    composerShown: !sessionsError && (creating ? canBuildApps : !!session),
    onFiles: (files) => composerRef.current?.addFiles(files),
  });

  return (
    <div
      role="dialog"
      aria-label={`Coding session — ${title}`}
      data-docked={chrome.docked ? 'true' : undefined}
      {...frameProps}
      {...fileDrop.handlers}
      // `items-stretch`, not `items-start` — same reasoning as the inbox zen
      // modal (frontend/src/features/chat/inbox-chat-modal.tsx): the panel
      // sets only a maxHeight, so top-aligning it let a short session open as
      // a small card pinned to the top of the screen. This window opens
      // straight out of a chat window, so the two must frame identically.
      className={chrome.docked ? 'absolute inset-0 flex items-stretch justify-center' : 'fixed inset-0 z-50 flex items-stretch justify-center'}
      // FRONT-3146: docked, the window layer supplies the seam and there is
      // no scrim; standalone it is the dimmed full-screen overlay.
      style={
        chrome.docked
          ? undefined
          : {
              // Same seam as the chat window (FRONT-3142): the app shell's own gap.
              padding: 'var(--frame-gap)',
              background: 'rgba(20, 20, 26, 0.42)',
              backdropFilter: 'blur(10px) saturate(0.9)',
              WebkitBackdropFilter: 'blur(10px) saturate(0.9)',
              animation: 'eddy-coding-bg-in 220ms ease-out',
            }
      }
    >
      <style>{`
        @keyframes eddy-coding-bg-in { from { opacity: 0 } to { opacity: 1 } }
        @keyframes eddy-coding-panel-in {
          from { opacity: 0; transform: translateY(8px) }
          to   { opacity: 1; transform: translateY(0) }
        }
        /* FRONT-2478: the interview bar's halo — a soft primary ring that
           breathes so the exit reads as the live thing on screen. */
        @keyframes eddy-interview-halo {
          0%, 100% { box-shadow: 0 0 0 3px color-mix(in oklch, var(--primary) 12%, transparent) }
          50%      { box-shadow: 0 0 0 6px color-mix(in oklch, var(--primary) 6%, transparent) }
        }
        /* Same scaling contract as the inbox zen modal: the reading column
           sets the base font-size from the Aa pref; markdown rides it in em
           so EDDY's prose grows together. Chrome stays on fixed tokens. */
        .eddy-reading .markdown-body { font-size: 1em; line-height: 1.65; }
        .eddy-reading .markdown-body h1 { font-size: 1.5em; }
        .eddy-reading .markdown-body h2 { font-size: 1.28em; }
        .eddy-reading .markdown-body h3,
        .eddy-reading .markdown-body h4 { font-size: 1.12em; }
      `}</style>

      <div
        onClick={(e) => e.stopPropagation()}
        data-testid="builder-window-panel"
        className="flex flex-col w-full overflow-hidden bg-card text-foreground border border-border"
        style={{
          // FRONT-3138: no width cap — the window spans the screen between
          // the backdrop's side gutters, as it already does top to bottom.
          // The reading-width pref sizes the conversation column inside
          // (`columnMax`); only the "Full" setting lets it run the whole width.
          // `100%` of the backdrop's padded box rather than a hand-computed
          // `calc(100vh - 112px)` that has to be kept in step with the padding
          // above it. The stretch does the sizing; this is the belt.
          maxHeight: '100%',
          minHeight: 420,
          borderRadius: 'var(--radius-xl-ds)',
          boxShadow: 'var(--shadow-lg)',
          animation: 'eddy-coding-panel-in 240ms ease-out',
          // Anchors the file-drop overlay to the panel.
          position: 'relative',
        }}
      >
        {fileDrop.active && <BuildFileDropOverlay />}
        {/* FRONT-2258: ONE 60px bar, from the Builder header design handoff.
            It replaces the old eyebrow row PLUS the tinted state strip under
            it. Two things moved rather than disappearing: the strip's state
            sentence is now the coloured lamp beside the wordmark (its hover
            card carries the words, the run's steps and its duration), and the
            strip's audience badge is now the Visibility control itself — the
            one element that both STATES who can open the app and CHANGES it,
            so the two can no longer describe it differently. */}
        <div className="flex items-center gap-3 h-[60px] px-4 shrink-0 border-b border-border">
          {/* FRONT-2379 fix-round: this crumb stays `shrink-0` end to end —
              unlike the chat window's crumb (inbox-chat-modal.tsx), it has no
              "started" badge sharing its left group to make room for, so
              there is nothing here for it to shrink away from. */}
          <div className="flex items-center gap-1.5 shrink-0" data-testid="builder-window-crumb">
            <EddyMark size={20} className="shrink-0 text-foreground" />
            {/* "Eddy / Builder" — what this window IS. The old "Build" pill
                said the same thing in a chip; the design states it as a path,
                which is quieter and leaves the row's weight to the lamp. */}
            <span aria-hidden className="text-[length:var(--text-md)] text-muted-foreground/50">
              /
            </span>
            <span className="text-[length:var(--text-md)] font-semibold tracking-[-0.2px] text-foreground whitespace-nowrap">
              Builder
            </span>
            {!creating && session && (
              <BuildLamp
                // FRONT-1898: display* fold in the manual-stop override, so the
                // lamp frees the instant the user clicks Stop — same reason the
                // strip took these rather than the raw tape values.
                status={displayStatus}
                reason={displayWaitingReason}
                activity={activity}
                runSummary={runSummary}
                lastActivityAt={session.lastActivityAt}
              />
            )}
          </div>
          {/* No project switcher in the header (eddy, 2026-10-26): an existing
              build is opened from the new-build screen's left column, or its
              tab in the top bar. */}
          {/* No "New" in the header: the top bar's Build always starts a new build (eddy, 2026-10-26). */}
          <div className="flex-1 min-w-[8px]" />
          {/* FRONT-2267: the right-hand controls are ONE group with one
              rhythm — `gap-1.5`, the same as the left cluster. They used to be
              loose children of the header row, so the row's own `gap-3` put
              12px between adjacent 34px icon buttons: enough air to read as
              unrelated items rather than one set of actions. The outer gap now
              separates the GROUPS, this one separates the icons. */}
          {/* FRONT-2935: a signal, so it stays visible rather than waiting for the pointer like the window actions. */}
          {!creating && session?.app && (
            <DraftUpdateButton gate={draftGate} pending={updatingDraft} onUpdate={() => void handleUpdateDraft()} />
          )}
          <div data-window-actions className="flex items-center gap-1.5 shrink-0">
            {/* The header is Preview, ONE cog and the three window buttons
                (eddy, 2026-10-26). Who can open the app, Save App,
                Configuration and the project actions all live behind the cog,
                in a panel inside this window. Preview only exists once the
                project is in Apps — before that there is no address to open. */}
            {!creating && session?.app && (
              // The shared eye (FRONT-3273, `components/builder-header/`): Pro Builder renders the same control.
              // eddy, 2026-10-26: a plain click opens the app in a window to the
              // right of this one. A modifier click, and the pop-out (no window
              // layer), keep the browser tab.
              // FRONT-3159: an editor's eye opens their own draft — a sealed preview, so always a browser tab.
              <HeaderPreviewLink
                href={previewEye(session.app).href}
                label={
                  previewEye(session.app).href !== session.app.url
                    ? previewEye(session.app).label
                    : chrome.docked
                      ? 'Preview — open this app beside the Build'
                      : 'Preview — open this app in a new tab'
                }
                onPlainClick={
                  // The app is captured at render (the eye only renders with one), so a taken-over click always opens it.
                  chrome.docked && previewEye(session.app).href === session.app.url
                    ? ((app, title) => () => openAppWindow({ slug: app.slug, url: app.url, title: title || app.slug }, chrome.windowId))(session.app, session.title)
                    : undefined
                }
              />
            )}
            {!creating && session?.app && ownerDraftLink(session.app) && (
              <Tip label="Preview draft — your changes in a locked preview, before they go live">
                <a
                  href={ownerDraftLink(session.app)!}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Preview draft"
                  className="h-[34px] shrink-0 inline-flex items-center gap-1.5 rounded-[10px] border border-border px-2.5 text-xs text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
                >
                  <Eye size={14} />
                  Preview draft
                </a>
              </Tip>
            )}
            {/* FRONT-1668 review fix, kept: reachable in EVERY state —
                creating, loading, error, or an attached session. Only the
                session-dependent parts of the panel are gated on `session`. */}
            <Tip label={settingsOpen ? 'Close settings' : 'Settings — sharing, save, configuration and project actions'}>
              <button
                type="button"
                onClick={() => setSettingsOpen((v) => !v)}
                aria-label="Build settings"
                aria-expanded={settingsOpen}
                data-testid="build-settings-toggle"
                className={cn(
                  'size-[34px] shrink-0 inline-flex items-center justify-center rounded-[10px] border border-border text-muted-foreground hover:text-foreground hover:bg-muted transition-colors active:scale-[0.96]',
                  settingsOpen && 'bg-muted text-foreground',
                )}
              >
                <Gear size={16} />
              </button>
            </Tip>
            {session && (
              <ProjectContextDialog
                key={session.id}
                sessionId={session.id}
                open={contextOpen}
                onOpenChange={setContextOpen}
              />
            )}
            {/* FRONT-3164: this build in its own browser window. */}
            <WindowPopOutButton
              kind="build"
              id={session?.id}
              title={session?.title ?? null}
              className="size-[34px] shrink-0 inline-flex items-center justify-center rounded-[10px] text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
            />
            <WindowMinimizeButton className="size-[34px] shrink-0 inline-flex items-center justify-center rounded-[10px] text-muted-foreground hover:text-foreground hover:bg-muted transition-colors" />
            <Tip label="Close (Esc) — EDDY keeps working in the background">
              <button
                type="button"
                onClick={onClose}
                aria-label="Close"
                className="size-[34px] shrink-0 inline-flex items-center justify-center rounded-[10px] text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
              >
                <X size={18} />
              </button>
            </Tip>
          </div>
        </div>

        {!creating && session?.appUnavailable && (
          <div
            data-testid="coding-app-unavailable"
            className="shrink-0 border-b border-border bg-amber-500/10 px-4 py-2 text-[length:var(--text-sm)] text-amber-600 dark:text-amber-300"
          >
            {session.appUnavailable}
          </div>
        )}

        {/* FRONT-3138: one row — your builds on the left, the conversation
            (or the new-project box) on the right — and each side scrolls on
            its own. The new-project screen and an open build share this
            frame, so moving between them changes the right side only. The
            rail belongs to the start screen only: once Start opens a build,
            it goes (eddy, 2026-10-26). */}
        <div ref={bodyRef} data-testid="build-body" className="@container relative flex flex-1 min-h-0 divide-x divide-border">
          {viewerImage && (
            <BuildImageViewer image={viewerImage} images={conversationPictures} onShow={setViewerImage} onClose={() => setViewerImage(null)} />
          )}
          {creating && (sessions?.length ?? 0) > 0 && !sessionsError && (
            <ExistingBuildsTimeline
              sessions={sessions ?? []}
              selectedId={null}
              mirroredEyebrow
              className={cn(
                'transition-opacity duration-300 motion-reduce:transition-none',
                // Above the reply box's own full-width background (z-10), which
                // otherwise paints over the middle of the list while lifted.
                buildsFloat && 'absolute inset-y-0 left-0 z-20 border-r border-border bg-card',
                leavingNew && 'pointer-events-none opacity-0',
              )}
              onOpen={(id) => {
                // One window per project: an already-open one comes
                // forward and this one closes (eddy, 2026-10-26).
                if (chrome.windowId && claimBuildWindow(chrome.windowId, id, sessions?.find((x) => x.id === id)?.title) === 'existing') return;
                setCreating(false);
                setSelectedId(id);
              }}
            />
          )}
        {/* Scrolling reading column */}
        {/* The scrollbar's room is kept from the start, on both sides so the column
            stays centred: when the history loads and starts to scroll, nothing
            jumps sideways (eddy, 2026-10-26). */}
        <div
          data-testid="build-conversation"
          // A floating list draws its own edge; the column's divider would sit at the window's edge.
          // A column, so the reply box can sit at the bottom of a short
          // conversation (`mt-auto` below) — where the new-project box slid to
          // (eddy, 2026-10-28) — and still pin there once it scrolls.
          className={cn('flex min-w-0 flex-1 flex-col overflow-y-auto [scrollbar-gutter:stable_both-edges]', buildsFloat && creating && '!border-l-0')}
        >
          {sessionsError ? (
            <CenteredNote tone="error">
              Could not load your coding projects: {(sessionsError as Error).message}
            </CenteredNote>
          ) : creating ? (
            <NewProjectView
              columnMax={columnMax}
              chatFont={chatFont}
              hasSessions={(sessions?.length ?? 0) > 0}
              composerRef={composerRef}
              onCreated={(id) => {
                // The new project's window is this one from now on.
                if (chrome.windowId) claimBuildWindow(chrome.windowId, id);
                setSelectedId(id);
                setCreating(false);
                setLeavingNew(false);
              }}
              onLeaving={setLeavingNew}
              onCancel={sessions && sessions.length > 0 ? () => setCreating(false) : undefined}
            />
          ) : sessionsLoading || (!session && !loadError) ? (
            <CenteredNote>
              <EddyLoadingMark size={16} /> Loading…
            </CenteredNote>
          ) : loadError ? (
            <CenteredNote tone="error">Could not open this session: {loadError}</CenteredNote>
          ) : (
            <OpenProjectFileContext.Provider value={session ? { sessionId: session.id, open: openProjectFile, showImage } : null}>
              <SessionTranscript
                columnMax={columnMax}
                chatFont={chatFont}
                title={session?.title ?? ''}
                repoPath={session?.repoPath ?? ''}
                projectSlug={session?.projectSlug ?? ''}
                transcript={transcript}
                sessionId={session?.id ?? null}
                openedAtSeq={openedAtSeq}
                summary={summary}
                status={status}
                queueBusy={actionsBusy}
                lastError={session?.lastError ?? null}
                paused={status === 'waiting' && waitingReason === 'paused'}
                onContinue={() =>
                  send('Continue').catch((err) => {
                    toast.error(sendFailureMessage(err, 'continue'));
                  })
                }
                uploads={uploads}
                projectContext={projectContext ?? null}
                onDeleteUpload={handleDeleteUpload}
                displayStatus={displayStatus}
                lastActivityAt={session?.lastActivityAt}
                onStopBuild={stopRun}
              />
            </OpenProjectFileContext.Provider>
          )}
          {/* Reply bar — matched to the reading column width, text-only, no rule
              above it. It lives INSIDE the scrolling column (eddy, 2026-10-26: "the
              reply box should be part of the chat"): pinned to the bottom at its
              normal size, and part of the flow once expanded, so the history above
              it can still be scrolled to. */}
          {!creating && !sessionsError && session && (
            <div
              data-testid="build-composer-area"
              className="sticky bottom-0 z-10 mt-auto bg-card has-[[data-expanded=true]]:static"
            >
              <div className="mx-auto" style={{ maxWidth: columnMax }}>
                <ChatInput
                  ref={composerRef}
                  // FRONT-1036: keyed by the coding session's own id, not `sessionId`
                  // (that prop also drives the chat model-mode toggle, a different
                  // domain concept this composer doesn't use — see hideModelSwitch).
                  draftKey={session.id}
                  onSubmit={(text, attachments) => {
                    // FRONT-1308: while a run is genuinely blocked on an open
                    // card, a plain send() would just 409 (the run hasn't
                    // finished — it's blocked on this exact answer) and strand
                    // the user's typed reply. Route it into the open batch
                    // instead, same as clicking an option on the card. This
                    // includes requiresOption batches (e.g. the publish gate):
                    // FRONT-1307 dropped that card's own Other field because
                    // the gate can't treat free text as a yes, but
                    // publish-tool.ts's answer reader already handles an
                    // `other`-only answer safely — it declines to publish and
                    // relays the user's words back to the agent to confirm, so
                    // this is the one path that gets them there rather than
                    // discarding a typed reply outright.
                    //
                    // FRONT-1708: `openQuestion` here is `blockingQuestion`
                    // (use-coding-session.ts), NOT every open card — once the
                    // run has wound down (timeout or interrupt) with the card
                    // still unanswered, nothing is blocked on it any more, and
                    // typing here must start a NEW run rather than silently
                    // become the answer to a batch nobody is waiting on.
                    // FRONT-3155: ONE rule for what Send does with an open batch
                    // (planComposerSend): chosen or skipped entries go out with
                    // the typed text as the person's note; with none chosen the
                    // typed text is the reply — the answer to a batch the run is
                    // blocked on (FRONT-1308/1708), an ordinary message otherwise.
                    const answerTarget = panelItem ?? openQuestion;
                    if (answerTarget) {
                      const plan = planComposerSend({
                        entries: panelItem && panelValue ? wizardEntries(panelValue) : [],
                        text,
                        questionCount: answerTarget.questions.length,
                        blocking: !!openQuestion,
                      });
                      if (plan.kind !== 'plain-message') {
                        const batchId = answerTarget.batchId;
                        // FRONT-1898: rethrow after toasting, same shape as send() below.
                        return submitComposerAnswer(
                          async () => {
                            const result = await answerQuestions(batchId, plan.body);
                            setSentBatchIds((ids) => [...ids, batchId]);
                            return result as { warning?: string } | undefined;
                          },
                          {
                            warning: (message) => toast.warning(message, { duration: 8000 }),
                            error: (message) => toast.error(message),
                          },
                        );
                      }
                    }
                    // FRONT-1898: rethrow after toasting so ChatInput's own
                    // submitPayload sees the rejection too and restores the
                    // typed text/attachments into the box instead of losing
                    // them — this is exactly what happened live on prod: a
                    // "coding-run limit (2)" rejection ate a real message with
                    // only a toast, no way to get the text back.
                    return send(text, attachments).catch((err) => {
                      toast.error(sendFailureMessage(err, 'composer'));
                      throw err;
                    });
                  }}
                  topSlot={
                    panelItem && panelValue
                      ? ({ text, focusSend }) => (
                          <CodingAnswerPanel
                            item={panelItem}
                            value={panelValue}
                            onChange={(value) => setAnswerDraft({ batchId: panelItem.batchId, value })}
                            typedText={text}
                            detachedAndBusy={panelDetachedAndBusy}
                            onDone={focusSend}
                          />
                        )
                      : undefined
                  }
                  sendGate={panelItem ? panelSendGate : undefined}
                  // The context meter sits next to Send, so the window spends no
                  // row of its own on it (eddy, 2026-10-26). Still always on
                  // screen: it is the number that says when to hand over.
                  beforeSend={<ContextMeter usage={contextUsage} />}
                  onStop={stopRun}
                  isBusy={composerBusy}
                  isStreaming={composerBusy}
                  // BUG-037 / FRONT-850: queueBusy is the poll-backed second opinion —
                  // it can un-stick the auto-fire if the SSE tape's own
                  // `status` ever freezes on "working" after a dropped stream.
                  // FRONT-1898: also folds in the manual-stop override, so a
                  // queued draft frees the moment the user clicks Stop instead
                  // of waiting on the server row to leave "running".
                  queueBusy={queueBusy}
                  // FRONT-1182: while the run is genuinely parked on an open
                  // card, this composer's own submit IS what unblocks it (the
                  // routing just above). Enter must therefore send rather than
                  // queue — the queue resolves on a busy -> idle edge that
                  // nothing but this submit can produce, so queueing here waits
                  // on itself forever. `openQuestion` is `blockingQuestion`, so
                  // a card nothing is waiting on any more does NOT set this and
                  // Enter keeps queueing normally (FRONT-1708).
                  submitResolvesTurn={!!openQuestion}
                  // FRONT-761: hold a queued draft when the run failed.
                  turnError={status === 'failed'}
                  hideLiveCall
                  hideImageGen
                  hideModelSwitch
                  // FRONT-3163: live-preview Markdown, images where they are dropped.
                  markdown
                  placeholder={
                    panelItem && panelValue
                      ? answerPlaceholder({
                          counts: wizardCounts(panelValue),
                          takesTypedAnswer: panelTakesTypedAnswer(panelItem),
                        })
                      : openQuestion
                        ? 'Type your answer…'
                        : 'What should EDDY build or change?'
                  }
                />
              </div>
            </div>
          )}
        </div>
        {settingsOpen && (
          <BuildSettingsPanel title={session && !creating ? session.title : 'Builder'} onClose={() => setSettingsOpen(false)}>
            {!creating && session && (
              <PanelSection title="App">
                {/* FRONT-2940: in an editor's own draft this place sends it to the owner for review; nothing an editor does moves the app. */}
                {session.app && publishControlFor(session) === 'ask' && (
                  <AskOwnerToAcceptButton
                    slug={session.app.slug}
                    ownerName={session.app.ownerName ?? "the app's owner"}
                    runBusy={actionsBusy}
                    className="w-full justify-center"
                  />
                )}
                {publishControlFor(session) === 'save' && (
                <>
                <Button
                  onClick={handleSaveApp}
                  disabled={saveApp.isPending || actionsBusy}
                  // The accessible name the specs address this control by
                  // (E2E-CODING-01/04/07) stays "Save App".
                  aria-label="Save App"
                  className="w-full justify-center gap-2"
                >
                  {saveApp.isPending ? <CircleNotch size={16} className="animate-spin" /> : <FloppyDisk size={16} />}
                  {session.app ? 'Save App' : 'Save to Apps'}
                </Button>
                <p className="text-xs text-muted-foreground">
                  {actionsBusy
                    ? 'Available once EDDY finishes the current run.'
                    : 'Stores this project in your Apps and makes it live at its address.'}
                </p>
                </>
                )}
                {session.app && (
                  <>
                    {/* FRONT-2258: the control that both STATES who can open
                        the app and CHANGES it. The wrapper keeps the old
                        audience badge's test id. */}
                    <div className="flex items-center justify-between gap-3 pt-1">
                      <span className="text-[length:var(--text-sm)]">Who can open it</span>
                      <span data-testid="coding-app-audience" className="shrink-0 inline-flex">
                        <AppSharePopover
                          slug={session.app.slug}
                          audience={appAudience(session.app)}
                          scope={session.app.shareScope ?? 'workspace'}
                          people={session.app.people ?? []}
                          pausedPeople={session.app.pausedPeople ?? []}
                          listed={session.app.listedToColleagues !== false}
                          url={session.app.url}
                          audienceLock={session.app.audienceLock ?? null}
                          live={session.app.status === 'live'}
                          onChanged={refreshApp}
                          size="full"
                          trigger="audience"
                        />
                      </span>
                    </div>
                    {/* FRONT-2091: reachability, which the audience word
                        deliberately does not carry. */}
                    {session.app.status !== 'live' && (
                      <p
                        data-testid="coding-app-offline"
                        className="flex items-start gap-1.5 rounded-lg border border-amber-500/30 bg-amber-500/10 px-2.5 py-2 text-xs text-amber-600 dark:text-amber-300"
                      >
                        <EyeSlash size={12} weight="fill" aria-hidden className="mt-0.5 shrink-0" />
                        Offline — only you can open it right now. Its sharing settings are unchanged and apply again the moment it's live.
                      </p>
                    )}
                    <PanelAction
                      icon={<LinkSimple size={16} />}
                      label="Copy app link"
                      hint="Copies this app's web address to your clipboard."
                      onClick={() => handleCopyAppLink(session.app!.url)}
                    />
                  </>
                )}
              </PanelSection>
            )}
            {!creating && session && (capabilitiesFlagOn || session.app) && (
              <PanelSection title="Configuration" hint="What EDDY may use while it builds, and what this app may use once it runs.">
                <ConfigurationSections
                  // The sessions LIST, not the attached `session`: a
                  // capability save invalidates the list, while the attached
                  // row is only re-read on tape events, so it goes stale
                  // after a save. But `app` only rides the single-session GET
                  // (a list row leaves it undefined), so it must still come
                  // from the attached row.
                  session={{ ...(sessions?.find((s) => s.id === session.id) ?? session), app: session.app }}
                  showBuilder={capabilitiesFlagOn}
                />
              </PanelSection>
            )}
            {!creating && session && (
              <PanelSection title="Project">
                <div className="-mx-2.5 flex flex-col">
                  {/* FRONT-1787: STANDING constraints — they ride every turn. */}
                  <PanelAction
                    icon={<NotePencil size={16} />}
                    label="Project context"
                    hint="Standing rules for this project. EDDY follows them in every message."
                    onClick={() => setContextOpen(true)}
                  />
                  <PanelAction
                    icon={<FloppyDisk size={16} />}
                    label="Save Conversation"
                    hint="Saves this chat to your Files. Save again to update that copy."
                    onClick={handleSave}
                    disabled={save.isPending}
                  />
                  <PanelAction
                    icon={<ClipboardText size={16} />}
                    label="Generate handover"
                    hint="Writes a short summary of this session so far. EDDY keeps it for Clear & continue."
                    onClick={handleHandover}
                    disabled={handover.isPending || actionsBusy}
                  />
                  <PanelAction
                    icon={<Eraser size={16} />}
                    label="Clear & continue"
                    hint="EDDY forgets this chat and carries on from the latest handover. Generate a handover first."
                    onClick={handleClear}
                    disabled={clear.isPending || actionsBusy}
                  />
                </div>
                <div className="flex flex-col gap-1.5 pt-1">
                  <span className="flex items-center gap-2 text-[length:var(--text-sm)]">
                    <ArrowsClockwise size={16} className={autoClearChoice !== 'off' ? 'text-primary' : 'text-muted-foreground'} />
                    Auto handover
                  </span>
                  <p className="text-xs text-muted-foreground">
                    When the context meter reaches this level, EDDY writes a handover and clears its memory by itself.
                  </p>
                  <div role="radiogroup" aria-label="Auto handover" className="flex flex-wrap gap-1">
                    {AUTO_CLEAR_CHOICES.map((choice) => (
                      <button
                        key={choice}
                        type="button"
                        role="radio"
                        aria-checked={autoClearChoice === choice}
                        disabled={autoClear.isPending}
                        onClick={() => handleAutoClearChoice(choice)}
                        className={cn(
                          'h-7 rounded-full border px-2.5 text-xs tabular-nums transition-colors disabled:opacity-50',
                          autoClearChoice === choice
                            ? 'border-primary bg-primary/10 text-foreground'
                            : 'border-border text-muted-foreground hover:bg-muted hover:text-foreground',
                        )}
                      >
                        {choice === 'off' ? 'Off' : `${choice}%`}
                        {/* FRONT-3301: the level this build uses without a pick of its own. */}
                        {isDefaultAutoClearChoice(choice, session) && (
                          <span className="ml-1.5 text-[length:var(--text-2xs)] text-muted-foreground">default</span>
                        )}
                      </button>
                    ))}
                  </div>
                </div>
                <div className="-mx-2.5 pt-1">
                  <PanelAction
                    icon={<Trash size={16} />}
                    label="Delete project"
                    hint={
                      session.app
                        ? "Deletes this project's conversation and files in your workspace. Cannot be undone. The published app stays as it is."
                        : "Deletes this project's conversation and files in your workspace. Cannot be undone."
                    }
                    onClick={() => void handleDelete()}
                    disabled={deleteSession.isPending}
                    destructive
                  />
                </div>
              </PanelSection>
            )}
            {/* FRONT-3146: person-level preferences (hover hints, ...) live in
                Settings -> Builder, which opens as a window beside this one. */}
            <PanelSection title="Builder">
              <div className="-mx-2.5">
                <PanelAction
                  icon={<Gear size={16} />}
                  label="Builder settings"
                  hint="Preferences for the Builder, like hover hints. Opens next to this window."
                  onClick={() => openSettingsWindow('builder', chrome.windowId)}
                  testId="coding-builder-settings"
                />
              </div>
            </PanelSection>
            {/* FRONT-2258: what this session has cost so far — only once a
                run has actually ended, so three zeros never read as a broken
                statistic. */}
            {!creating && session && sessionStats && (
              <div className="grid grid-cols-3 gap-1.5 rounded-lg bg-muted/60 p-2.5">
                <SessionStat label="Time" value={formatSessionDuration(sessionStats.durationMs)} />
                <SessionStat label="Turns" value={formatSessionTurns(sessionStats.turns)} />
                <SessionStat
                  label="Cost"
                  value={formatSessionCost(sessionCost)}
                  tip={sessionCost?.estimated ? ESTIMATE_HINT : undefined}
                />
              </div>
            )}
          </BuildSettingsPanel>
        )}
        </div>

      </div>
    </div>
  );
}

function CenteredNote({ children, tone }: { children: React.ReactNode; tone?: 'error' }) {
  return (
    <div
      className={cn(
        'h-full min-h-[280px] flex items-center justify-center gap-2 p-8 text-[length:var(--text-sm)]',
        tone === 'error' ? 'text-destructive' : 'text-muted-foreground',
      )}
    >
      {children}
    </div>
  );
}

/* --------------------------- build lamp (FRONT-2258) --------------------- */

/**
 * Seconds elapsed since `startedAt`, ticking while `live` — the lamp's run
 * timer. Returns null when there is nothing to count, so the caller shows no
 * timer at all rather than a stalled "0s".
 *
 * The interval is only armed while a run is actually in flight: a finished
 * session must not keep a 1Hz re-render alive behind a window the user has
 * stopped watching. A future clock (skew between server and browser) is
 * clamped to zero rather than shown as a negative age.
 */
function useElapsedMs(startedAt: string | null | undefined, live: boolean): number | null {
  const startMs = useMemo(() => {
    if (!startedAt) return null;
    const parsed = Date.parse(startedAt);
    return Number.isFinite(parsed) ? parsed : null;
  }, [startedAt]);

  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    if (startMs == null || !live) return;
    setNow(Date.now());
    const id = window.setInterval(() => setNow(Date.now()), 1_000);
    return () => window.clearInterval(id);
  }, [startMs, live]);

  if (startMs == null) return null;
  return Math.max(0, now - startMs);
}

/** FRONT-2721: `buildLiveness` against a clock that ticks only while a build runs. */
function useBuildLiveness(status: CodingStatus | null, lastActivityAt: string | null | undefined): BuildLiveness | null {
  const live = status === 'working' || status === 'checking';
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!live) return;
    setNow(Date.now());
    const id = window.setInterval(() => setNow(Date.now()), 1_000);
    return () => window.clearInterval(id);
  }, [live]);
  return buildLiveness({ status, lastActivityAt, now });
}

/**
 * The build state, as a single coloured lamp beside the wordmark (FRONT-2258).
 *
 * This is what the tinted status strip collapsed into. The strip spent a whole
 * row stating something that is true and unchanging for most of a session's
 * life; the lamp states it in eight pixels and keeps the detail one hover
 * away — the state in words, plus what the run cost.
 *
 * What the hover card says depends on the state, deliberately:
 *  - finished cleanly -> the run's own tally ("5 steps · 5m 43s"), the only
 *    state that HAS a completed tally. `formatRunSummary` returns null for
 *    anything else, so a failed or interrupted run can never be labelled with
 *    an earlier run's numbers.
 *  - anything else -> what Eddy is doing right now and for how long, the live
 *    half the strip used to show inline.
 *
 * The ticking timer stays OUT of `aria-label`: the label would otherwise
 * change every second, and a name that churns is worse than no detail at all
 * for anyone listening to it. The state sentence is the name; the numbers
 * live in the hover card.
 */
function BuildLamp({
  status,
  reason,
  activity,
  runSummary,
  lastActivityAt,
}: {
  status: CodingStatus | null;
  reason: WaitingReason | null;
  activity: RunActivity | null;
  runSummary: RunSummary | null;
  lastActivityAt: string | null | undefined;
}) {
  const running = status === 'working' || status === 'checking';
  const elapsedMs = useElapsedMs(activity?.startedAt, running);
  const liveness = useBuildLiveness(status, lastActivityAt);
  if (!status) return null;

  const stuck = liveness?.kind === 'stuck';
  const elapsed = elapsedMs == null ? null : formatDuration(elapsedMs);
  const label = stuck ? STUCK_LABEL : buildStatusLabel(status, reason);
  // Same gate as the strip's: only `done` has a completed run behind it.
  // `waiting` means a run is in flight and blocked on the user, so quoting a
  // tally there would replace live truth with one from minutes ago.
  let meta: string | null;
  if (status === 'done') meta = formatRunSummary(runSummary);
  else if (stuck) meta = liveness?.text ?? null;
  else meta = [activity?.label, elapsed].filter(Boolean).join(' · ') || null;

  const tone = lampTone(status, reason, stuck);

  return (
    <Tip
      label={
        <span className="flex flex-col gap-1">
          <span className="font-medium">{label}</span>
          {meta && <span className="tabular-nums opacity-70">{meta}</span>}
        </span>
      }
    >
      {/* The padded box is the hover target — an 8px dot is too small to
          point at. `role="img"` so the label is announced as the graphic's
          name; a live region here would re-announce on every timer tick. */}
      <span
        data-testid="coding-build-state"
        role="img"
        aria-label={label}
        className="inline-flex items-center p-1.5 shrink-0 cursor-default"
      >
        <span
          className={cn(
            'size-2 rounded-full ring-[3px] transition-[background-color,box-shadow] duration-300',
            tone,
            running && !stuck && 'animate-pulse',
          )}
        />
      </span>
    </Tip>
  );
}

/** One cell of the "…" menu's session totals block (FRONT-2258). */
function SessionStat({ label, value, tip }: { label: string; value: ReactNode; tip?: string }) {
  return (
    <Tip label={tip}>
      <div className="flex flex-col gap-0.5 min-w-0">
        <span className="text-[length:var(--text-2xs)] font-medium uppercase tracking-[0.3px] text-muted-foreground">
          {label}
        </span>
        <span className="text-[length:var(--text-xs)] font-semibold tabular-nums text-foreground truncate">
          {value}
        </span>
      </div>
    </Tip>
  );
}

/* ----------------------------- new project ------------------------------ */

function NewProjectView({
  columnMax,
  chatFont,
  hasSessions,
  composerRef,
  onCreated,
  onCancel,
  onLeaving,
}: {
  columnMax: ReadingMaxWidth;
  chatFont: number;
  hasSessions: boolean;
  /** The window's file drop feeds this box (FRONT-3138). */
  composerRef: React.RefObject<ChatInputHandle | null>;
  onCreated: (id: string) => void;
  onCancel?: () => void;
  /** FRONT-3223: the first message is on its way — the window fades the list of builds. */
  onLeaving?: (leaving: boolean) => void;
}) {
  const [name, setName] = useState('');
  // FRONT-3138: the opening beats of EDDY's interview, asked in the reply
  // box's question band. All optional.
  const [answers, setAnswers] = useState<WizardValue>(() => emptyWizardValue(NEW_PROJECT_QUESTIONS.length));
  // FRONT-2382: the optional "Design direction" section — inspiration text,
  // chosen directives, uploaded/picked reference images. Empty draft costs
  // nothing extra on submit (see buildDesignCreatePayload).
  const [design, setDesign] = useState<DesignDirectionDraft>(EMPTY_DESIGN_DRAFT);
  // FRONT-2382 review #3: Start refuses while a reference image is still
  // queued or uploading — a fast submit could otherwise beat the upload and
  // ship a project missing an image that still looked staged on screen.
  const [uploading, setUploading] = useState<UploadInFlight[]>([]);
  const designFlagOn = useFeatureFlag(CODING_DESIGN_DIRECTION_FLAG, false);
  const nameId = useId();
  const composerBoxRef = useRef<HTMLDivElement>(null);
  // FRONT-3223 (eddy, 2026-10-28): before the first message the reply box sits
  // mid-window — in the free space below the name and intro, never over them.
  // Sending opens the Build itself, where the box is at the bottom.
  const freeAreaRef = useRef<HTMLDivElement>(null);
  const introRef = useRef<HTMLDivElement>(null);
  const reworked = useUiRefactoring();
  const { lift: composerLift, animate: composerSlides, placing: composerPlacing } = useCenterLift(freeAreaRef, reworked, introRef);
  const expandDrop = useExpandDrop(composerLift);
  const [leaving, setLeaving] = useState(false);
  const create = useCreateCodingSession();
  const gate = newProjectStartGate({ creating: create.isPending, uploading: uploading.length > 0 });

  // FRONT-1787: the create route refuses a name this user already has a
  // project under (409 `project_exists`) and hands back the session to open
  // instead, because a repeat name reuses the same sandbox repo. Held as
  // state rather than read off `create.error` so the offer survives the user
  // editing the name afterwards.
  const [clash, setClash] = useState<{ sessionId: string; title: string } | null>(null);
  // FRONT-1450: app creation is builder-tier only. Gated here — the one place
  // every entry point funnels through — rather than on each button.
  const { canBuildApps } = useCanBuildApps();

  // FRONT-3138: Start in the reply box creates the project. What was typed
  // and answered becomes the up-front context; the box's attachments go along
  // as reference files. A rejection hands the text and files back to the box.
  const submit = useCallback(
    async (text: string, attachments: ChatAttachment[]) => {
      const trimmed = name.trim();
      if (gate.disabled) throw new Error(gate.reason ?? 'Not ready');
      setClash(null);
      const context = newProjectContext(text, answers);
      // FRONT-2382: omitted whenever the flag is off or nothing was given.
      const designPayload = buildDesignCreatePayload(design, designFlagOn);
      // FRONT-3223: as a chat's first message does, the box slides down (and
      // the list of builds fades) while the project is being created.
      setLeaving(true);
      onLeaving?.(true);
      const leftAt = Date.now();
      try {
        // An empty name is left out: the server gives the project a
        // placeholder and EDDY names it in his first reply.
        const data = await create.mutateAsync({
          ...(trimmed ? { name: trimmed } : {}),
          ...(context ? { context } : {}),
          ...(designPayload ? { design: designPayload } : {}),
          ...(attachments.length > 0 ? { attachments: attachments.map((a) => ({ fileId: a.fileId })) } : {}),
        });
        // Both writes are best-effort server-side, so a project can exist
        // without them. Saying so is the whole point of the server reporting it.
        if (data.contextWritten === false) {
          toast.warning("Project created, but EDDY didn't get your context", {
            description:
              'The workspace was busy. Add it from the “…” menu → Project context and he picks it up on his next run.',
            duration: 10000,
          });
        }
        if (data.referencesStaged === false) {
          toast.warning("Project created, but your files didn't make it in", {
            description: 'Attach them again in your first reply.',
            duration: 10000,
          });
        }
        // Let the slide down finish before the Build opens in its place, so
        // the box arrives where the opened Build's box sits.
        const rest = LIFT_SLIDE_MS - (Date.now() - leftAt);
        if (rest > 0) await new Promise((r) => setTimeout(r, rest));
        onCreated(data.session.id);
      } catch (err) {
        setLeaving(false);
        onLeaving?.(false);
        const body = (err as { body?: { code?: string; sessionId?: string; title?: string } }).body;
        if (body?.code === 'project_exists' && body.sessionId) {
          setClash({ sessionId: body.sessionId, title: body.title ?? trimmed });
        }
        throw err;
      }
    },
    [name, answers, design, designFlagOn, create, onCreated, onLeaving, gate.disabled, gate.reason],
  );

  if (!canBuildApps) {
    return (
      <div
        className="eddy-reading mx-auto w-full"
        style={{ maxWidth: columnMax, padding: '44px 1.25rem 28px', fontSize: chatFont }}
      >
        <Eyebrow label="Coding session" tag="needs access" />
        <ColumnTitle>Building apps needs a builder role</ColumnTitle>
        <p className="text-muted-foreground" style={{ margin: '0 0 26px' }}>
          Your account can use every app that&rsquo;s been shared with you, but not create new
          ones. Ask an admin to upgrade your access if you need to build.
        </p>
        {onCancel && (
          <Button variant="outline" size="sm" onClick={onCancel}>
            Back
          </Button>
        )}
      </div>
    );
  }

  return (
    // The column fills the window's height so the reply box sits at the
    // bottom, where it is in an open build, however short the page above it.
    <div className="flex min-h-full flex-col">
      {/* FRONT-3138: the same frame as an open build — eyebrow, title, a
          rule, then the conversation — with the title as the name field, so
          starting a project looks like the build it becomes. */}
      <div ref={freeAreaRef} className="w-full flex-1">
      <div
        ref={introRef}
        data-testid="new-project"
        className="eddy-reading mx-auto w-full"
        style={{ maxWidth: columnMax, padding: '44px 1.25rem 28px', fontSize: chatFont }}
      >
        <Eyebrow label="Coding session" tag="new project" />
        <label htmlFor={nameId} className="sr-only">
          Project name
        </label>
        <input
          id={nameId}
          autoFocus
          value={name}
          onChange={(e) => {
            setName(e.target.value);
            setClash(null);
          }}
          onKeyDown={(e) => {
            // Enter moves on to the reply box, where Start lives.
            if (e.key === 'Enter') {
              e.preventDefault();
              composerBoxRef.current?.querySelector('textarea')?.focus();
            }
          }}
          placeholder={hasSessions ? 'Name your new project' : 'Name your first project'}
          disabled={create.isPending}
          className="w-full bg-transparent font-semibold text-foreground placeholder:text-muted-foreground/45 focus:outline-none"
          style={{ fontSize: 'var(--text-3xl)', lineHeight: 1.15, letterSpacing: '-0.012em', margin: '0 0 6px' }}
        />
        {/* No "Optional — leave it empty…" line under the name (eddy, 2026-10-28):
            the placeholder already invites a name, and an empty one is fine. */}

        <div className="border-b border-border" style={{ paddingBottom: 26, marginBottom: 26 }} />

        {/* EDDY opens, as he does in a build — the copy must not promise
            fewer questions: he still interviews, he just starts further
            along (see GUIDED_BUILD_LINES). */}
        <div data-testid="new-project-greeting" className="flex items-start gap-3">
          <EddyMark size={26} />
          <p className="text-foreground [text-wrap:pretty]" style={{ margin: 0 }}>
            What are we building? Tell me in your own words below &mdash; as much or as little as
            you like. The quick questions are optional, and you can drop screenshots, spreadsheets
            or documents anywhere on this window. I&rsquo;ll start from there.
          </p>
        </div>

        {/* FRONT-2382: collapsed by default, flag-gated at the section boundary. */}
        {designFlagOn && (
          <div style={{ marginTop: 28 }}>
            <DesignDirectionDisclosure
              name={name}
              context=""
              value={design}
              onChange={setDesign}
              uploading={uploading}
              onUploadingChange={setUploading}
              disabled={create.isPending}
            />
          </div>
        )}

        {/* A name collision is not an error to report, it is a different
            action to offer — the project they almost certainly wanted. */}
        {clash ? (
          <div
            className="rounded-[10px] border border-border bg-muted/40 p-3
                       animate-in fade-in-0 slide-in-from-top-1 duration-200 ease-out"
            style={{ marginTop: 24 }}
          >
            <p className="text-[length:var(--text-sm)] text-foreground" style={{ margin: 0 }}>
              You already have a project called &ldquo;{clash.title}&rdquo;.
            </p>
            <p
              className="text-[length:var(--text-xs)] text-muted-foreground [text-wrap:pretty]"
              style={{ margin: '4px 0 0' }}
            >
              Opening it continues that codebase, with everything EDDY has already built.
              To start something separate, pick a different name.
            </p>
            <Button
              size="sm"
              className="transition-transform active:scale-[0.96]"
              style={{ marginTop: 10 }}
              onClick={() => onCreated(clash.sessionId)}
            >
              Open &ldquo;{clash.title}&rdquo;
            </Button>
          </div>
        ) : (
          create.isError && (
            <p className="text-[length:var(--text-sm)] text-destructive" style={{ margin: '24px 0 0' }}>
              {(create.error as Error).message}
            </p>
          )
        )}
      </div>
      </div>

      {/* The reply box, pinned like an open build's. */}
      <div
        data-testid="build-composer-area"
        data-centered={composerLift ? 'true' : undefined}
        className={`sticky bottom-0 z-10 bg-card ${liftTransition(composerSlides, composerPlacing)} has-[[data-expanded=true]]:static`}
        style={liftStyle(expandDrop.held || leaving ? 0 : composerLift)}
      >
        <div ref={composerBoxRef} className="mx-auto" style={{ maxWidth: columnMax }}>
          <ChatInput
            onExpandChange={expandDrop.onExpandChange}
            ref={composerRef}
            draftKey="coding:new-project"
            onSubmit={submit}
            onStop={() => {}}
            isBusy={false}
            submitLabel="Start"
            sendGate={() => ({ disabled: gate.disabled })}
            topSlot={({ send }) => (
              <div data-testid="new-project-questions">
                <AnswerWizard
                  questions={NEW_PROJECT_QUESTIONS}
                  value={answers}
                  onChange={setAnswers}
                  allowSkip
                  // The free-text answer is the reply box itself.
                  allowOther={false}
                  idBase={0}
                  disabled={create.isPending}
                  // Done on the last question starts the build, like Start.
                  onDone={send}
                />
              </div>
            )}
            hideLiveCall
            hideImageGen
            hideModelSwitch
            hideSlashMenu
            markdown
            placeholder="Let EDDY build anything for you…"
          />
          {/* Visible text, not a hover hint (FRONT-1670): Start is off
              exactly when this line has something to say. */}
          {gate.reason && (
            <p
              data-testid="new-project-start-reason"
              className="text-[length:var(--text-xs)] text-muted-foreground"
              style={{ margin: '-4px 16px 10px', textAlign: 'right' }}
            >
              {gate.reason}
            </p>
          )}
        </div>
      </div>
    </div>
  );
}

/* --------------------------- reading column bits ------------------------- */

function Eyebrow({ label, tag }: { label: string; tag: string }) {
  return (
    <div className="flex items-center gap-2.5 mb-4 uppercase text-[length:var(--text-xs)] font-medium tracking-[0.08em] text-muted-foreground">
      <span className="h-px bg-primary" style={{ width: 22 }} />
      <span className="text-primary font-semibold">{label}</span>
      <span className="text-muted-foreground/60">·</span>
      <span>{tag}</span>
    </div>
  );
}

function ColumnTitle({ children }: { children: React.ReactNode }) {
  return (
    <h1
      className="font-semibold text-foreground"
      style={{ fontSize: 'var(--text-3xl)', lineHeight: 1.15, letterSpacing: '-0.012em', margin: '0 0 14px' }}
    >
      {children}
    </h1>
  );
}

/**
 * FRONT-3175: EDDY's exec summary of the session, under the title. A summary
 * EDDY just rewrote on this open is typed out; a stored one simply shows.
 */
function SessionSummaryLine({ summary }: { summary: SessionSummaryState }) {
  const typed = useTypewriter(summary.text ?? '', summary.refreshed);
  if (!summary.text) {
    if (!summary.pending) return null;
    return (
      <p
        data-testid="coding-session-summary-pending"
        className="text-muted-foreground animate-pulse text-[length:var(--text-base)]"
        style={{ margin: '0 0 18px' }}
      >
        EDDY is writing a summary of this session…
      </p>
    );
  }
  return (
    <p
      data-testid="coding-session-summary"
      className="text-foreground/80 text-[length:var(--text-base)] [text-wrap:pretty]"
      style={{ margin: '0 0 18px', lineHeight: 1.55 }}
    >
      <span className="sr-only">{summary.text}</span>
      <span aria-hidden>
        {typed}
        {typed.length < summary.text.length && <span className="ml-px inline-block w-[2px] h-[1em] -mb-[2px] bg-foreground/60 animate-pulse" />}
      </span>
    </p>
  );
}

/** Reveals `text` a few characters per frame when `animate`, all at once otherwise (and under reduced motion). */
function useTypewriter(text: string, animate: boolean): string {
  const [shown, setShown] = useState(animate ? 0 : text.length);
  useEffect(() => {
    const reduced = typeof window !== 'undefined' && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
    if (!animate || reduced) {
      setShown(text.length);
      return;
    }
    setShown(0);
    const id = window.setInterval(() => {
      setShown((n) => {
        const next = Math.min(text.length, n + 3);
        if (next >= text.length) window.clearInterval(id);
        return next;
      });
    }, 24);
    return () => window.clearInterval(id);
  }, [text, animate]);
  return text.slice(0, shown);
}

/** FRONT-3175: the one line earlier history folds into on reopen. */
function HistoryFoldToggle({ label, unfolded, onToggle }: { label: string; unfolded: boolean; onToggle: () => void }) {
  return (
    <button
      type="button"
      data-testid="history-fold-toggle"
      aria-expanded={unfolded}
      onClick={onToggle}
      className="group flex w-full items-center gap-2 rounded-md border border-dashed border-border px-3 py-2 text-left text-[length:var(--text-sm)] text-muted-foreground hover:border-border hover:bg-muted/40 hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--eddy-blue)]"
    >
      <CaretRight size={12} weight="bold" className={cn('shrink-0 transition-transform', unfolded && 'rotate-90')} aria-hidden />
      <span className="flex-1 truncate">{unfolded ? 'Hide earlier history' : label}</span>
      {!unfolded && <span className="shrink-0 text-[length:var(--text-xs)] group-hover:text-foreground">Show everything</span>}
    </button>
  );
}

/* ----------------------------- session view ----------------------------- */

function SessionTranscript({
  columnMax,
  chatFont,
  title,
  repoPath,
  projectSlug,
  transcript,
  status,
  queueBusy,
  lastError,
  paused,
  onContinue,
  uploads,
  projectContext,
  onDeleteUpload,
  displayStatus,
  lastActivityAt,
  onStopBuild,
  sessionId,
  openedAtSeq,
  summary,
}: {
  columnMax: ReadingMaxWidth;
  chatFont: number;
  title: string;
  repoPath: string;
  projectSlug: string;
  transcript: TranscriptItem[];
  status: CodingStatus;
  queueBusy: boolean;
  lastError: string | null;
  /** FRONT-2972: the last run stopped at its step limit and can resume. */
  paused: boolean;
  onContinue: () => void;
  uploads: UploadAttachment[];
  /** FRONT-1787: the project's CODE-CONTEXT.md, or null when it has none. */
  projectContext: string | null;
  onDeleteUpload: (name: string) => void;
  /** FRONT-1898: folds in the manual-stop override, so the working line goes
   *  the instant Stop is clicked. */
  displayStatus: CodingStatus | null;
  lastActivityAt: string | null | undefined;
  onStopBuild: () => void;
  /** FRONT-3175: the open session, the tape position when it opened (what
   *  came before folds), and EDDY's exec summary for under the title. */
  sessionId: string | null;
  openedAtSeq: number | null;
  summary: SessionSummaryState;
}) {
  const bottomRef = useRef<HTMLDivElement>(null);
  const stickToBottomRef = useRef(true);
  // Scroll-follow tracks the LOCAL tape's visible status, not queueBusy — it
  // decides whether to auto-scroll to what's actually rendered on screen.
  const working = status === 'working' || status === 'checking';

  // FRONT-707: only the NEWEST question card accepts input — an older
  // unanswered card is history (its run moved on or was superseded).
  let newestQuestionSeq = 0;
  for (const item of transcript) {
    if (item.kind === 'questions') newestQuestionSeq = item.seq;
  }
  const groupedTranscript = useMemo(() => groupActivityRuns(transcript), [transcript]);
  const creations = useMemo(() => firstCreations(transcript), [transcript]);
  const fold = useMemo(() => foldEarlierHistory(groupedTranscript, openedAtSeq), [groupedTranscript, openedAtSeq]);
  // Unfolded stays unfolded for this visit; another session starts folded.
  const [unfoldedFor, setUnfoldedFor] = useState<string | null>(null);
  const unfolded = fold != null && unfoldedFor != null && unfoldedFor === sessionId;
  const shownTranscript = fold && !unfolded ? fold.visible : groupedTranscript;

  // Follow the live tail unless the user scrolled up to read something.
  // The scroll container is the modal body (our offsetParent chain), found
  // from the sentinel. FRONT-3146 (same bug FRONT-3142 fixed in the chat
  // window): the sentinel sits INSIDE the column's bottom padding, so
  // `scrollIntoView` on it never reaches the scroller's true end, and content
  // that settles after the first snap (markdown, images, fonts) grew the column
  // with nothing to re-scroll. Pin to the scroller's real maximum scroll
  // position instead, and re-apply it while the window is settling.
  const scrollToFloor = useCallback(() => {
    const scroller = bottomRef.current?.closest('.overflow-y-auto');
    if (scroller) scroller.scrollTop = bottomScrollTop(scroller);
  }, []);
  const itemCount = transcript.length;
  useEffect(() => {
    if (stickToBottomRef.current) scrollToFloor();
  }, [itemCount, working, scrollToFloor]);

  const columnRef = useRef<HTMLDivElement>(null);
  const settleUntilRef = useRef(Date.now() + OPEN_SETTLE_MS);
  useEffect(() => {
    const el = bottomRef.current?.closest('.overflow-y-auto');
    if (!el) return;
    const onScroll = () => {
      // A hidden (minimized) window reports a zero-height scroller and resets
      // its offset: that is not the reader scrolling away.
      if (el.clientHeight === 0) return;
      stickToBottomRef.current = el.scrollHeight - el.scrollTop - el.clientHeight < 150;
    };
    el.addEventListener('scroll', onScroll);
    // Content that grows after the first snap keeps the reader on the floor,
    // but only during the open settle window and only while still pinned.
    const column = columnRef.current;
    const ro =
      column && typeof ResizeObserver !== 'undefined'
        ? new ResizeObserver(() => {
            if (shouldRepinOnGrowth({ now: Date.now(), settleUntil: settleUntilRef.current, pinned: stickToBottomRef.current })) {
              el.scrollTop = bottomScrollTop(el);
            }
          })
        : null;
    if (column) ro?.observe(column);
    return () => {
      el.removeEventListener('scroll', onScroll);
      ro?.disconnect();
    };
  }, []);

  return (
    <div ref={columnRef} className="eddy-reading mx-auto w-full" style={{ maxWidth: columnMax, padding: '44px 1.25rem 28px', fontSize: chatFont }}>
      <Eyebrow label="Coding session" tag={projectSlug} />
      <ColumnTitle>{title}</ColumnTitle>
      <SessionSummaryLine summary={summary} />

      {/* Byline strip */}
      <div
        className="flex items-center gap-2.5 flex-wrap text-[length:var(--text-base)] text-muted-foreground border-b border-border"
        style={{ paddingBottom: 26, marginBottom: 26 }}
      >
        <span className="size-[26px] rounded-lg bg-foreground text-background inline-flex items-center justify-center font-semibold text-[length:var(--text-2xs)]">
          E
        </span>
        <span>
          Built with <strong className="text-foreground font-semibold">EDDY</strong> in your sandbox
        </span>
        <span className="text-muted-foreground/60">·</span>
        <span className="font-mono text-[length:var(--text-sm)]">{repoPath}</span>
        <span className="text-muted-foreground/60">·</span>
        <span className="inline-flex items-center gap-1 rounded-full border border-primary/25 bg-primary/10 px-2 py-0.5 text-[length:var(--text-xs)] font-medium text-primary">
          <Lightning size={10} weight="fill" /> coding
        </span>
      </div>

      {/* Uploads strip (FRONT-358) — the files currently staged in the
          project's uploads folder, mirroring the inbox modal's "In context"
          chips. The × removes the file from the repo; it's disabled while
          Eddy is working so files aren't yanked out from under the run. */}
      {uploads.length > 0 && (
        <div className="flex items-center gap-2 flex-wrap" style={{ margin: '-10px 0 26px' }}>
          <span className="uppercase text-[length:var(--text-xs)] font-medium tracking-[0.08em] text-muted-foreground">
            In the project
          </span>
          {uploads.map((u) => (
            <Tip key={u.path} label={`${u.name} — saved in the project at ${u.path}`}>
              <span
                className="inline-flex items-center gap-1.5 rounded-md border border-border bg-muted px-2 py-1 text-xs max-w-[260px]"
              >
                <Paperclip size={12} className="shrink-0" />
                <span className="truncate">{u.name}</span>
                <Tip label={
                    queueBusy
                      ? 'Available once EDDY finishes the current run'
                      : 'Delete from the project — removes the file from the repository'
                  }>
                  <button
                    type="button"
                    className="opacity-60 hover:opacity-100 shrink-0 disabled:opacity-30"
                    disabled={queueBusy}
                    onClick={() => onDeleteUpload(u.name)}
                    aria-label={`Delete ${u.name} from the project`}
                  >
                    <X size={12} />
                  </button>
                </Tip>
              </span>
            </Tip>
          ))}
        </div>
      )}

      {/* FRONT-1787: the project's standing context, pinned above the first
          message. Without it the only evidence the field did anything is
          whether Eddy's greeting happens to sound grounded, which is not
          evidence. Styled as a system note, not a chat bubble — the user did
          not say this in the conversation.

          Labelled "Project context", never "your context": FRONT-1831 lets
          Eddy rewrite this file, at which point "yours" would be false.
          Choosing the neutral label now avoids a rename later. */}
      {projectContext && (
        <div
          className="rounded-[10px] border border-border bg-muted/40 px-3.5 py-3"
          style={{ margin: '-10px 0 26px' }}
        >
          <p
            className="uppercase text-[length:var(--text-xs)] font-medium tracking-[0.08em] text-muted-foreground"
            style={{ margin: '0 0 6px' }}
          >
            Project context
          </p>
          <p
            className="text-[length:var(--text-sm)] text-foreground/85 whitespace-pre-wrap [text-wrap:pretty]"
            style={{ margin: 0 }}
          >
            {projectContext}
          </p>
        </div>
      )}

      {transcript.length === 0 && status !== 'working' && (
        <div className="text-muted-foreground">
          <p style={{ margin: 0 }}>Tell EDDY what to build.</p>
          <p className="text-[length:var(--text-sm)]" style={{ margin: '6px 0 0' }}>
            For example: “Build me a small page that shows a counter with two buttons.”
          </p>
        </div>
      )}

      <div className="space-y-4">
        {fold && (
          <HistoryFoldToggle
            label={foldLabel(fold)}
            unfolded={unfolded}
            onToggle={() => setUnfoldedFor(unfolded ? null : sessionId)}
          />
        )}
        {shownTranscript.map((item) => {
          // eddy, 2026-10-27: what these steps created shows under them, never folded away.
          const files =
            item.kind === 'activity-run' ? createdIn(item.items, creations) : item.kind === 'activity' ? createdIn([item], creations) : [];
          return (
            <Fragment key={item.seq}>
              {item.kind === 'activity-run' ? (
                <ActivityRunRow items={item.items} />
              ) : (
                <TranscriptRow item={item} newestQuestionSeq={newestQuestionSeq} runStatus={status} />
              )}
              {files.length > 0 && <CreatedFiles paths={files} />}
            </Fragment>
          );
        })}
        {status === 'failed' && lastError && (
          <div className="flex items-start gap-2 rounded-md border border-destructive/30 bg-destructive/10 px-3 py-2 text-[length:var(--text-sm)] text-destructive">
            <Warning size={16} className="mt-0.5 shrink-0" />
            <span>{lastError} — send a new message to try again.</span>
          </div>
        )}
        {paused && (
          <div className="flex items-center gap-3 text-[length:var(--text-sm)] text-muted-foreground">
            <span>EDDY used all its steps for this message. The work so far is kept.</span>
            <Button size="sm" variant="outline" disabled={queueBusy} onClick={onContinue}>
              Continue
            </Button>
          </div>
        )}
        <WorkingLine
          status={displayStatus}
          lastActivityAt={lastActivityAt}
          onStop={onStopBuild}
          onKindChange={() => {
            if (stickToBottomRef.current) scrollToFloor();
          }}
        />
      </div>
      <div ref={bottomRef} />
    </div>
  );
}

/** The build lamp's colour for one state. */
function lampTone(status: CodingStatus, reason: WaitingReason | null, stuck: boolean): string {
  if (stuck) return 'bg-amber-500 ring-amber-500/15';
  if (status === 'working') return 'bg-primary ring-primary/15';
  // FRONT-3084: the link already works while the background check runs.
  if (status === 'done' || status === 'checking') return 'bg-success ring-success/15';
  if (status === 'failed') return 'bg-destructive ring-destructive/15';
  // Waiting splits: the user being the blocker is something to act on (amber,
  // the app's one canonical warning tone), a deliberate stop is merely quiet.
  if (reason === 'answer') return 'bg-amber-500 ring-amber-500/15';
  return 'bg-muted-foreground/40 ring-muted-foreground/10';
}

/** FRONT-2721: the live working line, or the stuck state with its way out. */
function WorkingLine({
  status,
  lastActivityAt,
  onStop,
  onKindChange,
}: {
  status: CodingStatus | null;
  lastActivityAt: string | null | undefined;
  onStop: () => void;
  /** The stuck banner is taller than the working line, so the transcript re-follows its tail. */
  onKindChange: () => void;
}) {
  const liveness = useBuildLiveness(status, lastActivityAt);
  const kind = liveness?.kind;
  useEffect(() => {
    if (kind) onKindChange();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [kind]);
  if (!liveness) return null;
  if (liveness.kind === 'working') {
    return (
      <div
        data-testid="coding-working-line"
        className="flex items-center gap-2 text-[length:var(--text-sm)] text-muted-foreground tabular-nums"
      >
        <CircleNotch size={14} className="animate-spin shrink-0" /> {liveness.text}
      </div>
    );
  }
  return (
    <div
      data-testid="coding-stuck-line"
      role="status"
      className="flex flex-wrap items-center gap-x-3 gap-y-2 rounded-md border border-amber-500/30 bg-amber-500/10 px-3 py-2 text-[length:var(--text-sm)]"
    >
      <Warning size={16} className="shrink-0 text-amber-500" />
      <span className="flex-1 min-w-0">
        <span className="font-medium text-foreground">{liveness.text}</span>{' '}
        <span className="text-muted-foreground">{liveness.hint}</span>
      </span>
      <Button variant="outline" size="sm" onClick={onStop}>
        Stop build
      </Button>
    </div>
  );
}

/* ---------------------------- transcript rows ---------------------------- */

function TranscriptRow({
  item,
  newestQuestionSeq,
  runStatus,
}: {
  item: TranscriptItem;
  newestQuestionSeq: number;
  /** FRONT-2106: `deriveStatus`'s verdict, threaded down so a question card
   *  can close itself once its run is dead. The card's own pending rule is a
   *  per-item predicate — the `run_failed` boundary the backend now writes
   *  sits elsewhere on the tape and is invisible to it. */
  runStatus: CodingStatus;
}) {
  switch (item.kind) {
    case 'user':
      // Same bubble as the zen-chat conversation (message-bubble.tsx).
      // Upload badges (FRONT-358) stay on the bubble even after a file is
      // deleted from the project — the transcript records what happened.
      return (
        <div className="flex justify-end">
          <div className="max-w-[70%] bg-primary/10 [--md-table-ground:color-mix(in_oklab,var(--primary)_10%,var(--card))] border border-primary/40 px-3.5 py-2.5 rounded-xl text-sm text-foreground">
            <UserMessageText text={item.text} />
            {item.attachments.length > 0 && (
              <div className="mt-2 flex flex-wrap gap-1.5">
                {item.attachments.map((a) => (
                  <AttachmentChip key={a.path} name={a.name} path={a.path} />
                ))}
              </div>
            )}
          </div>
        </div>
      );
    case 'assistant':
      // FRONT-707: the build-plan beat — the assistant message opening with
      // the brief heading gets a distinct card so "here's what we're
      // building" reads as a moment, not just another paragraph.
      // FRONT-1846: `data-testid="coding-assistant-reply"` on both branches
      // is what lets a browser spec scope an assertion to Eddy's own
      // rendered prose — the collapsed <details><pre> an ActivityRow dumps
      // a tool's raw result into (below) carries no such testid, so a
      // locator scoped here can never match that hidden trace block.
      if (item.text.trimStart().startsWith('## Our build plan')) {
        return (
          <div
            data-testid="coding-assistant-reply"
            className="rounded-xl border border-primary/40 bg-primary/5 px-4 py-3"
          >
            <MarkdownRenderer content={item.text} />
          </div>
        );
      }
      // Eddy's prose reads like the document body — rides the Aa font pref.
      return (
        <div data-testid="coding-assistant-reply">
          <MarkdownRenderer content={item.text} />
        </div>
      );
    case 'activity':
      return <ActivityRow item={item} />;
    case 'questions':
      return (
        <CodingQuestionsCard
          item={item}
          interactive={item.seq === newestQuestionSeq}
          runStatus={runStatus}
        />
      );
    case 'marker': {
      const tone =
        item.variant === 'finished'
          ? 'text-success border-success/30'
          : item.variant === 'failed'
            ? 'text-destructive border-destructive/30'
            : 'text-muted-foreground border-border';
      return (
        <div className="flex items-center gap-3" aria-label={item.text}>
          <span className={cn('h-px flex-1 border-t', tone)} />
          <span className={cn('text-[length:var(--text-xs)] font-medium whitespace-nowrap', tone)}>{item.text}</span>
          <span className={cn('h-px flex-1 border-t', tone)} />
        </div>
      );
    }
  }
}

/**
 * The person's own message. Images they placed in the text (FRONT-3163) show
 * as small thumbnails, not full-size pictures (eddy, 2026-10-26); a click
 * opens the picture in the file window beside this Build.
 */
function UserMessageText({ text }: { text: string }) {
  const chrome = useWindowChrome();
  const files = useContext(OpenProjectFileContext);
  return (
    <div
      data-testid="user-message-text"
      className="[&_img]:my-1 [&_img]:inline-block [&_img]:size-20 [&_img]:cursor-pointer [&_img]:rounded-md [&_img]:border [&_img]:border-border [&_img]:object-cover [&_img:hover]:opacity-85"
      onClick={(e) => {
        const img = (e.target as HTMLElement).closest('img');
        // An "Attached: name" line (eddy, 2026-10-27) is a link to the file; it opens the same way.
        const link = img ? null : (e.target as HTMLElement).closest('a');
        const fileId = img
          ? uploadIdFromImageSrc(img.getAttribute('src'))
          : link
            ? uploadIdFromImageSrc(link.getAttribute('href'))
            : null;
        if (!fileId) return;
        const name = img ? img.getAttribute('alt') || 'Image' : (link?.textContent ?? '').replace(/^Attached:\s*/, '') || 'File';
        // A picture opens over this window (eddy, 2026-10-27); any other file in its own.
        if (img && files) {
          e.preventDefault();
          files.showImage({ src: img.getAttribute('src') ?? '', name });
          return;
        }
        if (!chrome.docked) return;
        e.preventDefault();
        openUploadWindow({ fileId, name }, chrome.windowId);
      }}
    >
      <MarkdownRenderer content={text} />
    </div>
  );
}

/**
 * A file attached to a message, as a chip that opens it in the file window to
 * the right (eddy, 2026-10-26). Attachments are staged into the project, so
 * this is the same window "Wrote …" rows open. Without a window to open into
 * it stays a plain label.
 */
function AttachmentChip({ name, path }: { name: string; path: string }) {
  const openFile = useContext(OpenProjectFileContext);
  const chip = (
    <>
      <Paperclip size={11} className="shrink-0" />
      <span className="truncate">{name}</span>
    </>
  );
  const cls =
    'inline-flex items-center gap-1 rounded-md border border-primary/25 bg-primary/10 px-1.5 py-0.5 text-[length:var(--text-xs)] text-foreground/80 max-w-[220px]';
  if (!openFile) {
    return (
      <Tip label={`${name} — uploaded into the project at ${path}`}>
        <span className={cls}>{chip}</span>
      </Tip>
    );
  }
  return (
    <Tip label={`Open ${name}`}>
      <button
        type="button"
        data-testid="message-attachment-open"
        onClick={() => openFile.open(path)}
        className={`${cls} cursor-pointer hover:bg-primary/20 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--eddy-blue)]`}
      >
        {chip}
      </button>
    </Tip>
  );
}

/**
 * The files a group of steps created (eddy, 2026-10-27): pictures as small
 * thumbnails that open over this Build, anything else as a chip that opens
 * the file in its window beside it. Without a session to read from it shows nothing.
 */
function CreatedFiles({ paths }: { paths: string[] }) {
  const openFile = useContext(OpenProjectFileContext);
  if (!openFile) return null;
  return (
    <div data-testid="created-files" className="flex flex-wrap items-end gap-2">
      {paths.map((path) => {
        const name = path.split('/').pop() || path;
        if (fileViewKind(path) === 'image') {
          return (
            <Tip key={path} label={`View ${name}`}>
              <button
                type="button"
                data-testid="created-file-image"
                onClick={() => openFile.open(path)}
                className="rounded-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--eddy-blue)]"
              >
                <img
                  src={projectFileUrl(openFile.sessionId, path)}
                  alt={name}
                  loading="lazy"
                  className="h-20 w-auto max-w-40 rounded-md border border-border object-cover hover:opacity-85"
                />
              </button>
            </Tip>
          );
        }
        return (
          <Tip key={path} label={`Open ${path}`}>
            <button
              type="button"
              data-testid="created-file-chip"
              onClick={() => openFile.open(path)}
              className="inline-flex max-w-[260px] cursor-pointer items-center gap-1.5 rounded-md border border-border bg-muted px-2 py-1 text-[length:var(--text-xs)] text-foreground/80 hover:bg-muted/70 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--eddy-blue)]"
            >
              <FileText size={12} className="shrink-0" />
              <span className="truncate">{name}</span>
            </button>
          </Tip>
        );
      })}
    </div>
  );
}

/**
 * FRONT-3171: the row's file control — a magnifier that opens the file in its
 * own window, and a close button while that window is open (eddy, 2026-10-26).
 * A boolean from the store, never a derived array (window-selectors.test.ts).
 */
function OpenFileToggle({ sessionId, path, onOpen }: { sessionId: string; path: string; onOpen: (path: string) => void }) {
  const id = fileWindowId(sessionId, path);
  const isOpen = useWindowStore((s) => s.windows.some((w) => w.id === id));
  const label = isOpen ? `Close ${fileWindowTitle(path)}` : `Open ${fileWindowTitle(path)}`;
  return (
    <Tip label={label}>
      <button
        type="button"
        data-testid="activity-open-file"
        data-open={isOpen ? 'true' : undefined}
        aria-label={label}
        onClick={(e) => {
          // The row itself expands on click; this control only opens or closes the file.
          e.preventDefault();
          e.stopPropagation();
          if (isOpen) useWindowStore.getState().close(id);
          else onOpen(path);
        }}
        className="ml-auto inline-flex size-6 shrink-0 items-center justify-center rounded-md text-[var(--eddy-blue-text)] hover:bg-[var(--eddy-blue-soft)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--eddy-blue)]"
      >
        {isOpen ? <X size={13} weight="bold" aria-hidden /> : <MagnifyingGlass size={13} weight="bold" aria-hidden />}
      </button>
    </Tip>
  );
}

/**
 * FRONT-3171: opens a project file in its own window. Provided by the Build
 * window around its transcript (it knows the session and its own window id);
 * absent → the rows simply have no Open control.
 */
const OpenProjectFileContext = createContext<{
  sessionId: string;
  open: (path: string) => void;
  /** Shows a picture over the Build window (eddy, 2026-10-27). */
  showImage: (image: BuildImage) => void;
} | null>(null);

/** A picture shown over the Build window. */
type BuildImage = ConversationImage;

/**
 * A picture over the Build window's body (eddy, 2026-10-27: "open screenshots
 * inside the window and not in a new window"). The header stays. A click on
 * the picture or beside it, the close button or Escape (the window's own
 * handler) puts the build back; the arrows, or the arrow keys, step through
 * every picture in the conversation and wrap at the ends.
 */
function BuildImageViewer({
  image,
  images,
  onShow,
  onClose,
}: {
  image: BuildImage;
  images: BuildImage[];
  onShow: (image: BuildImage) => void;
  onClose: () => void;
}) {
  const closeRef = useRef<HTMLButtonElement>(null);
  useEffect(() => closeRef.current?.focus(), [image.src]);
  const at = images.findIndex((img) => img.src === image.src);
  const canStep = at >= 0 && images.length > 1;
  const step = useCallback(
    (delta: number) => {
      if (canStep) onShow(images[stepIndex(at, delta, images.length)]);
    },
    [at, canStep, images, onShow],
  );
  useEffect(() => {
    const onKey = (e: globalThis.KeyboardEvent) => {
      if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') return;
      e.preventDefault();
      step(e.key === 'ArrowLeft' ? -1 : 1);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [step]);
  const iconButton =
    'size-[34px] shrink-0 inline-flex items-center justify-center rounded-[10px] text-muted-foreground hover:text-foreground hover:bg-muted transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--eddy-blue)]';
  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={image.name}
      data-testid="build-image-viewer"
      className="absolute inset-0 z-30 flex flex-col bg-card/95 backdrop-blur-sm animate-in fade-in-0 duration-150"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="flex shrink-0 items-center gap-1 px-4 py-2.5">
        <span className="min-w-0 flex-1 truncate text-[length:var(--text-sm)] text-muted-foreground">
          {image.name}
          {canStep && <span className="ml-2 tabular-nums">{at + 1} / {images.length}</span>}
        </span>
        {canStep && (
          <>
            <Tip label="Previous picture (←)">
              <button type="button" aria-label="Previous picture" data-testid="build-image-viewer-previous" onClick={() => step(-1)} className={iconButton}>
                <CaretLeft size={18} weight="bold" />
              </button>
            </Tip>
            <Tip label="Next picture (→)">
              <button type="button" aria-label="Next picture" data-testid="build-image-viewer-next" onClick={() => step(1)} className={iconButton}>
                <CaretRight size={18} weight="bold" />
              </button>
            </Tip>
          </>
        )}
        <Tip label="Close (Esc)">
          <button
            ref={closeRef}
            type="button"
            aria-label="Close picture"
            data-testid="build-image-viewer-close"
            onClick={onClose}
            className={iconButton}
          >
            <X size={18} />
          </button>
        </Tip>
      </div>
      <div
        className="flex min-h-0 flex-1 items-center justify-center px-6 pb-6"
        onClick={(e) => {
          if (e.target === e.currentTarget) onClose();
        }}
      >
        <img
          src={image.src}
          alt={image.name}
          data-testid="build-image-viewer-image"
          onClick={onClose}
          className="max-h-full max-w-full cursor-zoom-out rounded-md border border-border object-contain shadow-[var(--shadow-lg)]"
        />
      </div>
    </div>
  );
}

/**
 * Two or more actions in a row (eddy, 2026-10-26): only the newest shows,
 * under a toggle that unfolds the earlier ones in place. Failures among the
 * folded steps are counted on the toggle, so a red step is never hidden
 * without a trace.
 */
function ActivityRunRow({ items }: { items: ActivityItem[] }) {
  const [expanded, setExpanded] = useState(false);
  const shown = expanded ? items : items.slice(-1);
  return (
    <div data-testid="activity-run" data-expanded={expanded ? 'true' : undefined} className="space-y-1.5">
      <button
        type="button"
        data-testid="activity-run-toggle"
        aria-expanded={expanded}
        onClick={() => setExpanded((v) => !v)}
        className="inline-flex items-center gap-1 rounded px-1 text-[length:var(--text-xs)] text-muted-foreground hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--eddy-blue)]"
      >
        <CaretRight size={11} weight="bold" className={cn('shrink-0 transition-transform', expanded && 'rotate-90')} aria-hidden />
        {expanded ? 'Hide earlier actions' : earlierActionsLabel(items)}
      </button>
      {shown.map((item) => (
        <ActivityRow key={item.seq} item={item} />
      ))}
    </div>
  );
}

function ActivityRow({ item }: { item: Extract<TranscriptItem, { kind: 'activity' }> }) {
  const openFile = useContext(OpenProjectFileContext);
  const openPath = openableFile(item);
  const detail = [item.input && `Input:\n${item.input}`, item.resultContent && `Result:\n${item.resultContent}`]
    .filter(Boolean)
    .join('\n\n');
  return (
    <details className="group rounded-md border border-border/60 bg-muted/30 text-[length:var(--text-sm)]">
      <summary className="flex items-center gap-2 px-3 py-1.5 cursor-pointer select-none list-none [&::-webkit-details-marker]:hidden">
        {item.result === 'pending' ? (
          <CircleNotch size={13} className="animate-spin text-muted-foreground shrink-0" />
        ) : item.result === 'error' ? (
          <X size={13} className="text-destructive shrink-0" weight="bold" />
        ) : (
          <Check size={13} className="text-success shrink-0" weight="bold" />
        )}
        <span className="truncate font-mono text-[length:var(--text-xs)] text-muted-foreground group-open:text-foreground">
          {item.label}
        </span>
        {openPath && openFile && <OpenFileToggle sessionId={openFile.sessionId} path={openPath} onOpen={openFile.open} />}
      </summary>
      {detail && (
        <pre className="mx-3 mb-2 mt-1 max-h-64 overflow-auto rounded bg-background/60 p-2 text-[length:var(--text-xs)] whitespace-pre-wrap break-words text-muted-foreground">
          {detail}
        </pre>
      )}
    </details>
  );
}

/**
 * FRONT-3146: the reworked Build window. Inside the app shell it docks into the
 * shared window layer (see `features/windows/` and `InboxChatModal`, which
 * follows the same two-owner rule: the window host passes `windowId`, a
 * page-local mount — the Apps and Files pages — registers its own). Elsewhere it
 * is the full-screen overlay it always was.
 *
 * FRONT-3223: every caller opens the Build window through `CodingSessionModal`
 * in `coding-session-modal.tsx`, which picks this one with `eddy.ui-refactoring`
 * on. With it off that pick passes the old window as `children`, rendered in
 * the same shell so the window store's entry lifecycle is the same either way.
 */
export function BuildWindow({
  onClose,
  initialSessionId,
  windowId,
  startFresh,
  children,
}: {
  onClose: () => void;
  initialSessionId?: string;
  /** Set by the window host for a window it created; absent for a page-local mount. */
  windowId?: string;
  /** Open on the new-build step instead of resuming the latest build (the top bar's Build button). */
  startFresh?: boolean;
  /** The old window, when the flag is off; omitted, the reworked body renders. */
  children?: ReactNode;
}) {
  const localId = `local-build:${useId()}`;
  return (
    <WindowShell
      id={windowId ?? localId}
      kind="build"
      refId={initialSessionId ?? null}
      title="Build"
      hosted={windowId !== undefined}
      onDismiss={onClose}
      onClose={onClose}
    >
      {children ?? <CodingSessionModalBody onClose={onClose} initialSessionId={initialSessionId} startFresh={startFresh} />}
    </WindowShell>
  );
}
